import {AssetTypes, Frequencies, UploadAnalysisStatus, UploadStatuses} from "../../../models";
import {VerifiableUpload} from "./verifiableUpload";
import {LONGDATE} from "../constants";
import MyLittleForm, {
    getClientValue
} from "../../../app/components/client/application-stages/_common/uploads/v2/section-details/MyLittleForm";
import React, {useEffect, useMemo, useState} from "react";
import MortgageForm from "../../../app/components/data-capture/MortgageForm";
import dayjs from "dayjs";
import {getItemFromPath} from "../../../app/providers/mortgage/MortgageProvider";
import {capitaliseFirst, joinWithConjunction} from "../string-functions";
import {red} from "@ant-design/colors";
import {Alert, Button, Modal, Popconfirm, Table, Tag} from "antd";
import {performGQL, toleranceDifference} from "../../../app/api-functions/functions";
import {
    createAdminMortgageSectionVerificationNote,
    createAdminMortgageUploadVerifiedPoint,
    updateAdminMortgageUploadVerifiedPoint
} from "../../../graphql/mutations";
import {legacyIncome} from "../../../app/components/client/application-stages/_common/lender-tests/useXlsTests";
import ButtonAsync from "../../../app/components/elements/ButtonAsync";
import InternalNote
    from "../../../app/components/client/application-stages/_common/uploads/v2/section-details/InternalNote";
import DifferenceHighlighter from "../../../app/components/difference-highlighter/DifferenceHighlighter";
import useBackendUsers from "../../../app/providers/mortgage/useBackendUsers";

import {getUserUploadKey} from "../../../app/providers/mortgage/useMortgageUploadService";
import {API} from "aws-amplify";

import {BugOutlined, DeleteOutlined} from "@ant-design/icons";
import CustomPrimary from "../../../app/components/elements/CustomPrimary";

export const currentYear = new Date().getFullYear();
export const lastYear1 = currentYear - 1;
export const lastYear2 = currentYear - 2;
export const lastYear3 = currentYear - 3;

export async function updateUploadVerifiedRow(mortgage, row, pairs) {
    let input = {
        id: row.id, ...pairs,
        _version: row._version
    }
    let result = await performGQL({input}, updateAdminMortgageUploadVerifiedPoint)
    return result

}

export function sorterByDateName(name) {
    return (a, b) => {
        if (!a[name] || !dayjs.isDayjs(a[name])) {
            return -1
        }
        if (!b[name] || !dayjs.isDayjs(b[name])) {
            return 1
        }
        return a[name].isBefore(b[name]) ? -1 : a[name].isAfter(b[name]) ? 1 : 0;
        // if (a[name] < b[name]) return -1
        // if (a[name] > b[name]) return 1
        // return 0
    }
}

export const uploadExcludeFilter = it => !it.exclude && ![
    UploadStatuses.EXPIRED, UploadStatuses.REJECTED
].includes(it.status)

export function dataPointRow(upload, name) {
    if (!upload.verifiedData) {
        return null
    }
    return upload.verifiedData.items.find(item => item.name === name)
}

export function valueOrNoData(upload, name) {
    let row = dataPointRow(upload, name)
    return !!row ? getClientValue(row.verifiedValue, row.verifiedValueType) : null
}

export function getDataPointRow(upload, name) {
    let row = dataPointRow(upload, name)
    let value = !!row ? getClientValue(row.verifiedValue, row.verifiedValueType) : null
    return {
        row,
        name,
        value
    }
}

export const askInFormAnswerTypes = {
    date: 'DATE',
    int: 'NUMBER',
    text: 'STRING',
    textarea: 'STRING',
    currency: 'NUMBER',
    select: 'STRING',
    check: 'BOOLEAN'
}

export function getVerificationValueType(value, type) {
    switch (type) {
        case 'DATE':
            if (!!value) {
                if (typeof value === 'string') {
                    value = dayjs(value, 'YYYY-MM-DD')
                }

                if (value?.isValid()) {
                    return [
                        value.format('YYYY-MM-DD'), 'DATE'
                    ]
                }
            }

            return [
                null, 'DATE'
            ]
        case 'NUMBER':
            return [
                value ? value.toString() : 0, 'NUMBER'
            ]
        case 'BOOLEAN':
            return [
                !value ? "FALSE" : "TRUE", 'BOOLEAN'
            ]
        case 'STRING':
            return [
                value ? value.toString() : '', 'STRING'
            ]
        default:
            if (value !== 0 && !value) {
                return [
                    null, 'NULL'
                ]
            }
            if (typeof value === 'number') {
                return [
                    value.toString(), 'NUMBER'
                ]
            }
            if (typeof value === 'boolean') {
                return [
                    !value ? "FALSE" : "TRUE", 'BOOLEAN'
                ]
            }
            if (dayjs.isDayjs(value)) {
                if (value?.isValid()) {
                    return [
                        value.format('YYYY-MM-DD'), 'DATE'
                    ]
                }
                return [
                    null, 'NULL'
                ]
            }
            if (typeof value === 'string') {
                return [
                    value, 'STRING'
                ]
            }
    }

}

const getMinimumsForSection = (item) => {
    // the requirements for the section, based on the lender, whether on soft aipClass in Preapproval or in Apply

    // current accounts and saving statements
    if (item.name.startsWith('current-accounts')) {
        return {
            maxAge: [
                5, 'week'
            ],
            requiredRange: [
                6, 'months'
            ]
        }
    }
    // saving statements
    if (item.name.startsWith('assets') && !item.isInvestment) {
        return {
            maxAge: [
                5, 'week'
            ],
            requiredRange: [
                6, 'months'
            ]
        }
    }
    // investment statements
    if (item.name.startsWith('assets') && item.isInvestment) {
        return {
            maxAge: [
                5, 'week'
            ]
        }
    }

    // credit card statements
    if (item.name.startsWith('debts') && item.isCredit) {
        return {
            maxAge: [
                5, 'week'
            ],
            requiredRange: [
                6, 'months'
            ]
        }
    }
    // loan statements
    if (item.name.startsWith('debts') && !item.isCredit) {

        return {
            maxAge: [
                5, 'week'
            ],
            requiredRange: [
                6, 'months'
            ]
        }
    }

    // mortgage statements
    if (item.name.startsWith('properties')) {
        return {
            maxAge: [
                5, 'week'
            ],
            requiredRange: [
                6, 'months'
            ]
        }
    }

    // proof of address
    if (item.name.startsWith('proof-of-address')) {
        return {
            maxAge: [
                6, 'month'
            ],
        }
    }

    // identification
    if (item.name.startsWith('identification')) {
        return {
            maxAge: [
                6, 'month'
            ],
        }
    }

    // salary cert
    if (item.name.startsWith('salary-cert')) {
        return {
            maxAge: [
                6, 'month'
            ],
        }
    }

    // employment summary
    if (item.name.startsWith('employment-summary')) {
        return {
            maxAge: [
                2, 'year'
            ],
            requiredRange: [
                3, 'year'
            ]
        }
    }

    // payslips
    if (item.name.startsWith('payslips')) {
        return {
            maxAge: [
                5, 'week'
            ],
            requiredRange: [
                3, 'months'
            ]
        }
    }

    // our gift certificate
    if (item.name.startsWith('gift-certificate')) {
        return {
            maxAge: [
                6, 'month'
            ],
        }
    }

    // real gift certificate
    if (item.name.startsWith('lender-gift-certificate')) {
        return {
            maxAge: [
                6, 'month'
            ],
        }
    }

    // residence permit
    if (item.name.startsWith('residence-permit')) {
        return {
            maxAge: [
                -1, 'week'
            ],
        }
    }
    return {
        maxAge: [
            6, 'month'
        ]
    }
}
export const RenderErrors = ({errors}) => {
    if (!errors.length) {
        return null
    }
    let message = (<ul className="d-col gap-3 m-0 ps-15">
        {errors.map((it, i) => <li key={it}>{it}</li>)}
    </ul>)
    return (<Alert size="small" key={errors.join('|')} message={message} type="error"/>)
}
const compressString = (address) => {
    if (!address) {
        return ''
    }

    return address.trim().toLowerCase().replace(/[^a-zA-Z0-9]/g, '')
}

const getFirstRequiredDate = (section, rows) => {
    let last = rows[rows.length - 1]
    let requiredFirst = null
    if (last) {
        let dateToUse = last.hasOwnProperty('endDate') ? last.endDate : last.hasOwnProperty('documentDate') ? last.documentDate : last.expiryDate

        if (dayjs.isDayjs(dateToUse)) {

            let {
                maxAge,
                requiredRange
            } = getMinimumsForSection(section)
            let betterEndDate = dateToUse.add(1, 'day')
            let [m, ageUnit] = maxAge

            if (!requiredRange) {

                requiredFirst = dayjs().subtract(m, ageUnit)
            } else {

                let [n, rangeUnit] = requiredRange
                // how old is the last document?
                let age = dayjs().diff(betterEndDate, ageUnit, true)

                //if too old,
                //let lastDateToWorkWith = age > m ? dayjs().subtract(2, 'week') : dateToUse.add(1, 'day')
                let lastDateToWorkWith = dateToUse.add(1, 'day')

                requiredFirst = lastDateToWorkWith.subtract(n, rangeUnit)
            }

        }

    }
    return requiredFirst
}

export function getConfigurationTemplates() {
    const investmentStatementConfig = {
        template: {
            items: [
                {
                    name: 'accountNumber',
                    label: 'Account Number',
                    type: 'text'
                }, {
                    name: 'documentDate',
                    label: 'Start Date',
                    type: 'date',
                }, {
                    name: 'closingBalance',
                    label: 'Closing Balance',
                    type: 'currency',
                },
            ]
        },
        sortDate: 'documentDate',
        uploadsSummaryColumns: [
            {
                title: 'Date',
                key: 'documentDate',
                dataIndex: 'documentDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Account Number',
                key: 'accountNumber',
                dataIndex: 'accountNumber'
            }, {
                title: 'Closing Balance',
                key: 'closingBalance',
                dataIndex: 'closingBalance',
                align: 'right',
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let rows = uploads.map(it => ({
                key: it.id,
                accountNumber: valueOrNoData(it, 'accountNumber'),
                documentDate: valueOrNoData(it, 'documentDate'),
                closingBalance: valueOrNoData(it, 'closingBalance'),
                upload: it
            })).map((item, i) => {
                let errors = []
                if (!dayjs.isDayjs(item.documentDate)) {
                    errors.push(`Date missing`)
                }
                if (typeof item.accountNumber !== 'string') {
                    errors.push(`Account Number missing`)
                }
                if (isNaN(item.closingBalance)) {
                    errors.push(`Closing Balance missing`)
                }
                return {
                    ...item,
                    errors
                }
            }).sort(sorterByDateName('documentDate'))

            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getSectionErrors: (rows, mortgage, minimums, section) => {
            let errors = []
            if (rows.every(it => !!it.tooOld)) {
                errors.push(`All statements are older than ${minimums.maxAge[0]} ${minimums.maxAge[1]}s`)
            }
            return errors
        },
        getRowLabel: (row) => !!row && !!row.documentDate && `${row.documentDate.format(LONGDATE)}`,
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <MyLittleForm key={`investment-statement-documentDate-${uploadId}`}
                          item={managerGet('documentDate')}/>,
            <MyLittleForm key={`investment-statement-accountNumber-${uploadId}`}
                          item={managerGet('accountNumber')}/>,
            <MyLittleForm key={`investment-statement-closingBalance-${uploadId}`}
                          item={managerGet('closingBalance')}/>
        ],
        SectionApplicationForm: ({section}) => {
            let target = section.applicant === 1 ? `applicant1.assets[${section.index}]` : `applicant2.assets[${section.index}]`
            let questions = [
                {
                    name: 'currency',
                    label: 'Currency',
                    answer: {
                        type: 'text'
                    },
                    target: `${target}.currency`
                }
            ]
            return <MortgageForm questions={questions}/>
        },

    }
    const accountStatementConfig = {
        analysisPropertyName: "statement",
        template: {
            items: [
                {
                    name: 'iban',
                    label: 'IBAN',
                    type: 'text',
                    scan: 'accountNumber'
                }, {
                    name: 'openingBalance',
                    label: 'Opening Balance',
                    type: 'currency',
                    scan: 'openingBalance',
                    optional: true
                }, {
                    name: 'closingBalance',
                    label: 'Closing Balance',
                    type: 'currency',
                    scan: 'closingBalance',
                    optional: true
                }, {
                    name: 'startDate',
                    label: 'Start Date',
                    type: 'date',
                    scan: 'startDate',
                    optional: true
                }, {
                    name: 'endDate',
                    label: 'End Date',
                    type: 'date',
                    scan: 'endDate',
                    optional: true
                }, {
                    name: 'vault1OpeningBalance',
                    label: 'Opening Balance',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'vault1ClosingBalance',
                    label: 'Closing Balance',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'vault2OpeningBalance',
                    label: 'Opening Balance',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'vault2ClosingBalance',
                    label: 'Closing Balance',
                    type: 'currency',
                    optional: true
                }
            ]
        }, // add custom items to the template
        customTemplate: (that) => {

            let applicant = that.section.applicant === 1 ? that.mortgage.applicant1 : that.mortgage.applicant2

            let investments = applicant.assets.filter(it => it.assetType === AssetTypes.INVESTMENT)
            return {
                items: investments.map((it, i) => ({
                    name: `toInvestment-${it.id}`,
                    label: `Total To ${it.description}`,
                    type: 'currency',
                    optional: true
                })),
                uploadDataForms: investments.map((it, i) => `toInvestment-${it.id}`)
            }
        },
        sortDate: 'startDate',
        uploadsSummaryColumns: [
            {
                title: 'Start Date',
                key: 'startDate',
                dataIndex: 'startDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'End Date',
                key: 'endDate',
                dataIndex: 'endDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Opening Balance',
                key: 'openingBalance',
                dataIndex: 'openingBalance',
                align: 'right',
            }, {
                title: 'Closing Balance',
                key: 'closingBalance',
                dataIndex: 'closingBalance',
                align: 'right',
            }, {
                title: 'Mths',
                key: 'monthsInBetween',
                dataIndex: 'monthsInBetween',
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {

            let applicant = section.applicant === 1 ? mortgage.applicant1 : mortgage.applicant2
            let investments = applicant.assets.filter(it => it.assetType === AssetTypes.INVESTMENT)
            let iban = section.data?.accountNumber ||''
            let rows = uploads.map(it => {
                let totalToInvestments = 0
                let toInvestments = {}
                investments.forEach(investment => {
                    let name = `toInvestment-${investment.id}`
                    let value = valueOrNoData(it, name)
                    if (!!value || value === 0) {
                        totalToInvestments += value
                        toInvestments[investment.id] = value
                    }
                })
                return {
                    key: it.id, // iban: valueOrNoData (it, 'iban'),
                    upload: it,
                    startDate: valueOrNoData(it, 'startDate'),
                    endDate: valueOrNoData(it, 'endDate'),
                    startDateRow: getDataPointRow(it, 'startDate').row,
                    endDateRow: getDataPointRow(it, 'endDate').row,
                    openingBalance: valueOrNoData(it, 'openingBalance'),
                    closingBalance: valueOrNoData(it, 'closingBalance'),
                    vault1OpeningBalance: valueOrNoData(it, 'vault1OpeningBalance'),
                    vault1ClosingBalance: valueOrNoData(it, 'vault1ClosingBalance'),
                    vault2OpeningBalance: valueOrNoData(it, 'vault2OpeningBalance'),
                    vault2ClosingBalance: valueOrNoData(it, 'vault2ClosingBalance'),
                    iban: valueOrNoData(it, 'iban'),
                    totalToInvestments,
                    toInvestments
                }
            }).sort(sorterByDateName('startDate'))

            let requiredFirst = getFirstRequiredDate(section, rows)

            rows = rows.map((item, i) => {
                let errors = []
                if (!dayjs.isDayjs(item.startDate)) {
                    errors.push(`Start Date missing`)
                }
                if (!dayjs.isDayjs(item.endDate)) {
                    errors.push(`End Date missing`)
                }
                if (isNaN(item.openingBalance)) {
                    errors.push(`Opening Balance missing`)
                }
                if (isNaN(item.closingBalance)) {
                    errors.push(`Closing Balance missing`)
                }
                if (typeof item.iban !== 'string') {
                    errors.push(`IBAN missing`)
                }
                if (!iban || (typeof item.iban === 'string' && item.iban.replace(/[^a-zA-Z0-9]/g, '') !== iban.replace(/[^a-zA-Z0-9]/g, ''))) {
                    let str = !iban ? 'No Iban in application form' : ' should be ' + iban
                    errors.push(`IBAN mismatch: ${str}`)
                }
                let tooOld = requiredFirst ? dayjs.isDayjs(item.endDate) && item.endDate.isBefore(requiredFirst) : false

                if (rows.length > 1) {
                    let nextRow = rows[i + 1]
                    let nextOpenBalance = nextRow?.openingBalance
                    let nextStartDate = nextRow?.startDate
                    if (!isNaN(item.endDate) && nextStartDate && nextStartDate.isBefore(item.endDate)) {
                        errors.push(`New End Date Needed: ${nextStartDate.subtract(1, 'day').format(LONGDATE)}`)
                    } else if (nextOpenBalance && !isNaN(nextOpenBalance) && !isNaN(item.closingBalance)) {
                        let diff = Math.abs(nextOpenBalance - item.closingBalance)
                        if (diff > 1) {
                            errors.push(`Missing statement after`)
                        }
                    }
                }
                let trueOpeningBalance = (item.openingBalance || 0) + (item.vault1OpeningBalance || 0) + (item.vault2OpeningBalance || 0)
                let trueClosingBalance = (item.closingBalance || 0) + (item.vault1ClosingBalance || 0) + (item.vault2ClosingBalance || 0)

                return {
                    ...item,
                    openingBalance: trueOpeningBalance,
                    closingBalance: trueClosingBalance,
                    monthsInBetween: dayjs.isDayjs(item.endDate) && dayjs.isDayjs(item.startDate) && item.endDate.add(1, 'day').diff(item.startDate, 'months', true).toFixed(1),
                    errors,
                    tooOld
                }

            })
            return rows
        },
        getRowLabel: (values) => {
            return !!values.startDate && dayjs.isDayjs(values.endDate) && `${values.startDate.format(LONGDATE)} - ${values.endDate.format(LONGDATE)}`
        },
        getSectionErrors: (rows, mortgage, minimums, section) => {
            if (!rows.length) {
                return []
            }
            let sorted = rows.sort(sorterByDateName('startDate'))
            let errors = []

            // see if this account is duplicated anythere else
            let accountNo = section?.data?.accountNumber?.replace(/[^a-zA-Z0-9]/g)
            let matching = mortgage.bankAccountNumbers.filter(it => it.accountNumber?.replace(/[^a-zA-Z0-9]/g) === accountNo)
            if (matching.length > 1) {
                let strings = matching.map(it => {
                    return `Applicant ${it.applicant} ${it.source}`
                }).join(' and ')
                errors.push(`${strings} have same account number`)
            }

            let first = sorted[0]
            let last = sorted[sorted.length - 1]
            if (dayjs.isDayjs(first.startDate) && dayjs.isDayjs(last.endDate)) {

                // then check the range of the documents for
                // a) the last doc is not too old - use doc date
                // b) the last doc is too old - use today
                let thisAccount = section.data
                // if (section.name === 'current-accounts-1') {
                //     thisAccount = mortgage.applicant1.accounts.find(it => it.id === section.index)
                // }
                // if (section.name === 'current-accounts-2') {
                //     thisAccount = mortgage.applicant2.accounts.find(it => it.id === section.index)
                // }
                // if (section.name === 'assets-1') {
                //     thisAccount = mortgage.applicant1.assets.find(it => it.id === section.index)
                // }
                // if (section.name === 'assets-2') {
                //     thisAccount = mortgage.applicant2.assets.find(it => it.id === section.index)
                // }

                let isNewAccount = thisAccount.isNewAccount
                let isClosed = thisAccount.isClosed
                let betterEndDate = last.endDate.add(1, 'day')
                let {
                    maxAge,
                    requiredRange
                } = minimums
                let [n, rangeUnit] = requiredRange
                let [m, ageUnit] = maxAge
                // Error on endDate too old
                let age = dayjs().diff(betterEndDate, ageUnit, true)
                if (age > m && !isClosed) {
                    errors.push(`Most recent statement is older than ${m} ${ageUnit} (already in app)`)
                }

                //Error on gaps between statements
                let lastDateToWorkWith = age > m ? dayjs().subtract(2, 'week') : last.endDate.add(1, 'day')

                let today = dayjs()
                let rowsWithGapInDaysToNext = sorted.map((it, ix) => {
                    let next = sorted[ix + 1]
                    let daysToNext = 0
                    if (next) {
                        daysToNext = next.startDate.diff(it.endDate, 'days')
                    } else {
                        // if the most recent statement error is not already there, show the missing days to today
                        if (!(age > m && !isClosed)) {
                            // daysToNext = today.diff(it.endDate, 'days')
                        }

                    }
                    return {
                        ...it,
                        daysToNext
                    }
                })

                let requiredFirst = lastDateToWorkWith.subtract(n, rangeUnit)
                let missingRanges = []
                if (!isNewAccount) {
                    if (requiredFirst.isBefore(first.startDate)) {
                        missingRanges.push(`[ ${requiredFirst.format('D MMM')} - ${first.startDate.format('D MMM')} ]`)
                    }
                }
                missingRanges = [
                    ...missingRanges, ...rowsWithGapInDaysToNext.flatMap((it, ix) => {
                        if (it.daysToNext > 1) {
                            return [`[ ${it.endDate.add(1, 'day').format('D MMM')} - ${it.endDate.add(it.daysToNext - 1, 'days').format('D MMM')} ]`]
                        }
                        return []
                    })
                ]

                if (missingRanges.length) {
                    errors.push(`Missing statement${missingRanges > 1 ? 's' : ''} for ${joinWithConjunction(missingRanges, 'and')}`)
                }
            }
            return errors
        },
        saveScanDataToSection: async (mortgage, section, data) => {
            let target = section.applicant === 1 ? `applicant1` : `applicant2`
            if (section.name.startsWith('assets')) {
                target = `${target}.assets`
            } else {
                target = `${target}.accounts`
            }
            let source = `${target}[${section.index}].accountNumber`
            let val = getItemFromPath(mortgage, source)
            if (!val) {
                let update = {
                    target: `${target}[${section.index}]`,
                    pairs: {
                        accountNumber: data.iban, //accountAddress: data.accountAddress,
                    }
                }
                await mortgage.mutate({update})
            }
        },
        SummaryMessages: ({
                              rows,
                              mortgage,
                              minimums,
                              section
                          }) => {
            let errors = accountStatementConfig.getSectionErrors(rows, mortgage, minimums, section)
            return errors.map((it, i) => <Alert key={it} message={it} type="error"/>)
        },
        UploadDataForm: ({
                             uploadId,
                             managerGet,
                             restOfForm = []
                         }) => [
            <MyLittleForm key={`account-statement-iban${uploadId}`} item={managerGet('iban')}/>,
            <div key={`account-statement-group1-${uploadId}`} className="d-row gap-15 j-equal">
                <MyLittleForm key={`account-statement-startDate${uploadId}`} item={managerGet('startDate')}/>
                <MyLittleForm key={`account-statement-openingBalance${uploadId}`} item={managerGet('openingBalance')}/>
            </div>, <div key={`account-statement-group2-${uploadId}`} className="d-row gap-15 j-equal">
                <MyLittleForm key={`account-statement-endDate${uploadId}`} item={managerGet('endDate')}/>
                <MyLittleForm key={`account-statement-closingBalance${uploadId}`} item={managerGet('closingBalance')}/>
            </div>, <div key={`account-statement-group3-${uploadId}`} className="d-row gap-15 j-equal">
                <div className="d-col gap-3">
                    <strong className="text-center">Vault 1</strong>
                    <MyLittleForm key={`account-statement-vault1OpeningBalance${uploadId}`}
                                  item={managerGet('vault1OpeningBalance')}/>
                    <MyLittleForm key={`account-statement-vault1ClosingBalance${uploadId}`}
                                  item={managerGet('vault1ClosingBalance')}/>
                </div>
                <div className="d-col gap-3">
                    <strong className="text-center">Vault 2</strong>
                    <MyLittleForm key={`account-statement-vault2OpeningBalance${uploadId}`}
                                  item={managerGet('vault2OpeningBalance')}/>
                    <MyLittleForm key={`account-statement-vault2ClosingBalance${uploadId}`}
                                  item={managerGet('vault2ClosingBalance')}/>
                </div>
            </div>, ...restOfForm
        ],
        SectionApplicationForm: ({
                                     section,
                                     mortgage
                                 }) => {
            let target = section.applicant === 1 ? `applicant1` : `applicant2`
            if (section.name.startsWith('assets')) {
                target = `${target}.assets`
            } else {
                target = `${target}.accounts`
            }
            let salariedAcExists = section.applicant === 1 ? mortgage.applicant1.accounts.find(it => !!it.salaried) : mortgage.applicant2.accounts.find(it => !!it.salaried)
            let jointQuestions = [
                {
                    name: 'joint',
                    label: 'Joint Acc',
                    answer: {
                        type: 'select',
                        choices: [
                            {
                                value: true,
                                text: 'Yes'
                            }, {
                                value: false,
                                text: 'No'
                            }
                        ]

                    },
                    target: `${target}[${section.index}].joint`
                }, {
                    name: 'currency',
                    label: 'Currency',
                    answer: {
                        type: 'text'
                    },
                    target: `${target}[${section.index}].currency`
                },
            ]
            if (!section.name.startsWith('assets')) {
                if (!salariedAcExists || salariedAcExists.id === section.index) {
                    jointQuestions.push({
                        name: 'salaried',
                        label: 'Salaried',
                        answer: {
                            type: 'select',
                            choices: [
                                {
                                    value: true,
                                    text: 'Yes'
                                }, {
                                    value: false,
                                    text: 'No'
                                }
                            ]
                        },
                        target: `${target}[${section.index}].salaried`
                    })
                }
            }
            let questions = [
                [{
                    name: 'institution',
                    label: 'Bank Name',
                    answer: {
                        type: 'text',
                    },
                    target: `${target}[${section.index}].institution`
                }, {
                    name: 'accountNumber',
                    label: 'IBAN',
                    answer: {
                        type: 'text',
                    },
                    target: `${target}[${section.index}].accountNumber`
                }], jointQuestions, [

                    {
                        name: 'isClosed',
                        label: 'Is Closed',
                        answer: {
                            type: 'select',
                            choices: [
                                {
                                    value: true,
                                    text: 'Yes'
                                }, {
                                    value: false,
                                    text: 'No'
                                }
                            ]
                        },
                        target: `${target}[${section.index}].isClosed`
                    }, {
                        name: 'isNewAccount',
                        label: 'Is New Account',
                        answer: {
                            type: 'select',
                            choices: [
                                {
                                    value: true,
                                    text: 'Yes'
                                }, {
                                    value: false,
                                    text: 'No'
                                }
                            ]

                        },
                        target: `${target}[${section.index}].isNewAccount`
                    }

                ]
            ]
            if (section.name.startsWith('current-accounts')) {
                let hasOverdraftRow = [
                    {
                        name: 'hasOverdraft',
                        label: 'Has Overdraft',
                        answer: {
                            type: 'select',
                            choices: [
                                {
                                    value: true,
                                    text: 'Yes'
                                }, {
                                    value: false,
                                    text: 'No'
                                }
                            ]
                        },
                        target: `${target}[${section.index}].hasOverdraft`
                    }, {
                        name: 'overdraftLimit',
                        label: 'Overdraft Limit',
                        answer: {
                            type: 'euro'
                        },
                        target: `${target}[${section.index}].overdraftLimit`,
                        hides: (form) => form.getFieldValue('hasOverdraft') !== true
                    }
                ]
                questions.push(hasOverdraftRow)

                let rentAc = section.applicant === 1 ? mortgage.applicant1.accounts.find(it => !!it.accommodationDD) : mortgage.applicant2.accounts.find(it => !!it.accommodationDD)
                if (!rentAc || rentAc.id === section.index) {
                    let target2 = section.applicant === 1 ? `applicant1` : `applicant2`
                    let lastTwo = [
                        {
                            name: 'accommodationDD',
                            label: 'Accommodation DD',
                            answer: {
                                type: 'select',
                                choices: [
                                    {
                                        value: true,
                                        text: 'Yes'
                                    }, {
                                        value: false,
                                        text: 'No'
                                    }
                                ]
                            },
                            target: `${target}[${section.index}].accommodationDD`
                        }, {
                            name: 'monthlyAccommodationCosts',
                            label: 'Monthly Accommodation Costs',
                            answer: {
                                type: 'euro'
                            },
                            target: `${target2}.financial.monthlyAccommodationCosts`,
                        }
                    ]
                    questions.push(lastTwo)
                }
            }

            return <MortgageForm questions={questions}/>
        },
    }
    const cardStatementConfig = {
        template: {
            items: [
                {
                    name: 'accountNumber',
                    label: 'Account Number',
                    type: 'text',
                }, {
                    name: 'documentDate',
                    label: 'Document Date',
                    type: 'date',
                }, {
                    name: 'outstandingBalance',
                    label: 'Outstanding',
                    type: 'currency',
                }, {
                    name: 'creditLimit',
                    label: 'Credit Limit',
                    type: 'currency',
                }
            ]
        },
        sortDate: 'documentDate',
        uploadsSummaryColumns: [
            {
                title: 'Date',
                key: 'documentDate',
                dataIndex: 'documentDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Account Number',
                key: 'accountNumber',
                dataIndex: 'accountNumber'
            }, {
                title: 'Outstanding',
                key: 'outstandingBalance',
                dataIndex: 'outstandingBalance',
                align: 'right',
            }, {
                title: 'Credit Limit',
                key: 'creditLimit',
                dataIndex: 'creditLimit',
                align: 'right',
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let rows = uploads.map(it => ({
                key: it.id,
                accountNumber: valueOrNoData(it, 'accountNumber'),
                documentDate: valueOrNoData(it, 'documentDate'),
                outstandingBalance: valueOrNoData(it, 'outstandingBalance'),
                creditLimit: valueOrNoData(it, 'creditLimit'),
                upload: it
            })).sort(sorterByDateName('documentDate'))
            let iban = section.data.accountNumber

            rows = rows.map((item, i) => {
                let errors = []
                if (!dayjs.isDayjs(item.documentDate)) {
                    errors.push(`Doc Date missing`)
                }
                if (isNaN(item.outstandingBalance)) {
                    errors.push(`Outstanding Balance missing`)
                }
                if (typeof item.accountNumber !== 'string') {
                    errors.push(`Account Number missing`)
                }
                if (!iban || (typeof item.accountNumber === 'string' && item.accountNumber.replace(/[^a-zA-Z0-9]/g, '') !== iban.replace(/[^a-zA-Z0-9]/g, ''))) {
                    errors.push(`Account No mismatch`)
                }

                if (rows.length > 1) {
                    let nextStartDate = rows[i + 1]?.documentDate
                    if (nextStartDate && dayjs.isDayjs(nextStartDate) && dayjs.isDayjs(item.documentDate)) {
                        let diff = nextStartDate.diff(item.documentDate, "months", true)
                        if (diff > 1.1) {
                            errors.push(`Missing statement after`)
                        }
                    }
                }

                return {
                    ...item,
                    monthsInBetween: 1,
                    errors
                }

            })
            let requiredFirst = getFirstRequiredDate(section, rows, 'documentDate')
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getRowLabel: (row) => !!row && !!row.documentDate && `${row.documentDate.format(LONGDATE)}`,
        getSectionErrors: (rows, mortgage, minimums, section) => {
            if (!rows.length) {
                return []
            }
            let sorted = rows.sort(sorterByDateName('documentDate'))
            let errors = []
            let additionalQuestions = []
            let first = sorted[0]
            let last = sorted[sorted.length - 1]
            let account = section.data
            if (last.creditLimit !== account.creditLimit) {
                errors.push(`Credit Limit mismatch`)
                additionalQuestions.push({
                    name: 'creditLimit',
                    label: 'Credit Limit',
                    answer: {
                        type: 'euro',
                    },
                    target: `applicant${section.applicant}.debts[${section.index}].creditLimit`
                })
            }
            if (dayjs.isDayjs(first.documentDate)) {
                let betterEndDate = last.documentDate.add(1, 'day')
                let {
                    maxAge,
                    requiredRange
                } = minimums
                let [n, rangeUnit] = requiredRange
                let [m, ageUnit] = maxAge
                // test the age of the most recent document
                let age = dayjs().diff(betterEndDate, ageUnit, true)

                if (age > m && !account.isClosed) {
                    errors.push(`Most recent statement is older than ${m} ${ageUnit} (already in app)`)
                }
                // then check the range of the documents for
                // a) the last doc is not too old - use doc date
                // b) the last doc is too old - use today

                if (!account.isNewAccount) {
                    let lastDateToWorkWith = age > m && !account.isClosed ? dayjs().subtract(2, 'week') : last.documentDate.add(1, 'day')
                    let requiredFirst = lastDateToWorkWith.subtract(n, rangeUnit)
                    let whatWeHave = sorted.map(it => it.documentDate.format('MMMM'))
                    if (n > 0) {
                        let missingMonths = []
                        for (let i = 1; i <= n; i++) {
                            let date = requiredFirst.add(i, 'months')
                            if (!whatWeHave.includes(date.format('MMMM'))) {
                                missingMonths.push(date.format('MMMM'))
                            }
                        }
                        if (missingMonths.length) {
                            errors.push(`Missing statements for ${joinWithConjunction(missingMonths, 'and')}`)
                        }
                    }
                }
            }
            if (additionalQuestions.length) {
                return {
                    errors,
                    additionalQuestions
                }
            }
            return errors
        },
        SummaryMessages: ({
                              rows,
                              mortgage,
                              minimums,
                              section
                          }) => {
            let errors = cardStatementConfig.getSectionErrors(rows, mortgage, minimums, section)
            return errors.map((it, i) => <Alert key={it} message={it} type="error"/>)
        },
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <MyLittleForm key={`card-statement-documentDate-${uploadId}`} item={managerGet('documentDate')}/>,
            <MyLittleForm key={`card-statement-accountNumber-${uploadId}`}
                          item={managerGet('accountNumber')}/>,
            <MyLittleForm key={`card-statement-outstandingBalance-${uploadId}`}
                          item={managerGet('outstandingBalance')}/>,
            <MyLittleForm key={`card-statement-creditLimit-${uploadId}`} item={managerGet('creditLimit')}/>
        ],
        SectionApplicationForm: ({
                                     section,
                                     additionalQuestions
                                 }) => {
            let target = section.applicant === 1 ? `applicant1.debts[${section.index}]` : `applicant2.debts[${section.index}]`
            let questions = [
                {
                    name: 'accountNumber',
                    label: 'Account Number',
                    answer: {
                        type: 'text'
                    },
                    target: `${target}.accountNumber`
                }
            ]
            let mainRow = [
                {
                    name: 'isNewAccount',
                    label: 'Is New Account',
                    answer: {
                        type: 'select',
                        choices: [
                            {
                                value: true,
                                text: 'Yes'
                            }, {
                                value: false,
                                text: 'No'
                            }
                        ]

                    },
                    target: `${target}.isNewAccount`
                }, {
                    name: 'isClosed',
                    label: 'Is Closed',
                    answer: {
                        type: 'select',
                        choices: [
                            {
                                value: true,
                                text: 'Yes'
                            }, {
                                value: false,
                                text: 'No'
                            }
                        ]
                    },
                    target: `${target}.isClosed`
                }, {
                    name: 'currency',
                    label: 'Currency',
                    answer: {
                        type: 'text'
                    },
                    target: `${target}.currency`
                }
            ]
            if (additionalQuestions && additionalQuestions.length) {
                questions.push(additionalQuestions)
            }
            questions.push(mainRow)
            return <MortgageForm questions={questions}/>
        },
    }
    const loanStatementConfig = {
        template: {
            items: [
                {
                    name: 'iban',
                    label: 'IBAN',
                    type: 'text',
                    scan: 'iban'
                }, {
                    name: 'openingBalance',
                    label: 'Opening Balance',
                    type: 'currency',
                    scan: 'openingBalance'
                }, {
                    name: 'closingBalance',
                    label: 'Closing Balance',
                    type: 'currency',
                    scan: 'closingBalance'
                }, {
                    name: 'startDate',
                    label: 'Start Date',
                    type: 'date',
                    scan: 'startDate'
                }, {
                    name: 'endDate',
                    label: 'End Date',
                    type: 'date',
                    scan: 'endDate'
                }, {
                    name: 'monthlyPayment',
                    label: 'Monthly Payment',
                    type: 'currency',
                },
            ]
        },
        sortDate: 'startDate',
        uploadsSummaryColumns: [
            {
                title: 'Start Date',
                key: 'startDate',
                dataIndex: 'startDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'End Date',
                key: 'endDate',
                dataIndex: 'endDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Opening Balance',
                key: 'openingBalance',
                dataIndex: 'openingBalance',
                align: 'right',
            }, {
                title: 'Closing Balance',
                key: 'closingBalance',
                dataIndex: 'closingBalance',
                align: 'right',
            }, {
                title: 'Mths',
                key: 'monthsInBetween',
                dataIndex: 'monthsInBetween',
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let thisAccount = section.data
            let iban = thisAccount.accountNumber
            let rows = uploads.map(it => ({
                key: it.id, // iban: valueOrNoData (it, 'iban'),
                startDate: valueOrNoData(it, 'startDate'),
                endDate: valueOrNoData(it, 'endDate'),
                startDateRow: getDataPointRow(it, 'startDate').row,
                endDateRow: getDataPointRow(it, 'endDate').row,
                openingBalance: valueOrNoData(it, 'openingBalance'),
                closingBalance: valueOrNoData(it, 'closingBalance'),
                iban: valueOrNoData(it, 'iban'),
                upload: it
            })).sort(sorterByDateName('startDate'))
            rows = rows.map((item, i) => {
                let errors = []
                if (!dayjs.isDayjs(item.startDate)) {
                    errors.push(`Start Date missing`)
                }
                if (!dayjs.isDayjs(item.endDate)) {
                    errors.push(`End Date missing`)
                }
                if (isNaN(item.openingBalance)) {
                    errors.push(`Opening Balance missing`)
                }
                if (isNaN(item.closingBalance)) {
                    errors.push(`Closing Balance missing`)
                }
                if (typeof item.iban !== 'string') {
                    errors.push(`IBAN missing`)
                }
                if (!iban || (typeof item.iban === 'string' && item.iban.replace(/[^a-zA-Z0-9]/g, '') !== iban.replace(/[^a-zA-Z0-9]/g, ''))) {
                    errors.push(`IBAN mismatch`)
                }
                if (rows.length > 1) {
                    let nextRow = rows[i + 1]
                    let nextOpenBalance = nextRow?.openingBalance
                    let nextStartDate = nextRow?.startDate
                    if (!isNaN(item.endDate) && nextStartDate && nextStartDate.isBefore(item.endDate)) {
                        errors.push(`New End Date: ${nextStartDate.subtract(1, 'day').format(LONGDATE)}`)
                    } else if (nextOpenBalance && !isNaN(nextOpenBalance) && !isNaN(item.closingBalance)) {
                        let diff = Math.abs(nextOpenBalance - item.closingBalance)
                        if (diff > 1) {
                            errors.push(`Missing statement after`)
                        }
                    }

                }
                return {
                    ...item,
                    monthsInBetween: dayjs.isDayjs(item.endDate) && dayjs.isDayjs(item.startDate) && item.endDate.add(1, 'day').diff(item.startDate, 'months', true).toFixed(1),
                    errors
                }

            })
            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
            return rows
            // let rows =  uploads.map(it => ({
            //     key          : it.id,
            //     accountNumber: valueOrNoData(it, 'accountNumber'),
            //     documentDate : valueOrNoData(it, 'documentDate'),
            // })).sort(sorterByDateName('documentDate'))
            // rows = rows.map((item, i) => {
            //     let errors = []
            //     if (!dayjs.isDayjs(item.documentDate)) {
            //         errors.push(`Doc Date missing`)
            //     }
            //     if (typeof item.accountNumber !== 'string') {
            //         errors.push(`Account Number missing`)
            //     }
            //     if (rows.length > 1) {
            //         let nextStartDate = rows[i + 1]?.documentDate
            //         if (nextStartDate && dayjs.isDayjs(nextStartDate) && dayjs.isDayjs(item.documentDate)) {
            //             let diff = nextStartDate.diff(item.endDate, "days")
            //             if (diff > 2) {
            //                 errors.push(`Missing statement?`)
            //             }
            //         }
            //     }
            //
            //     return {
            //         ...item,
            //         monthsInBetween: 1,
            //         errors
            //     }
            //
            // })
            // return rows
        },
        getRowLabel: (values) => {
            return !!values.startDate && !!values.endDate && `${values.startDate.format(LONGDATE)} - ${values.endDate.format(LONGDATE)}`
        },
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <MyLittleForm key={`account-statement-iban${uploadId}`} item={managerGet('iban')}/>,
            <div key={`account-statement-group1-${uploadId}`} className="d-row gap-15 j-equal">
                <MyLittleForm key={`account-statement-startDate${uploadId}`} item={managerGet('startDate')}/>
                <MyLittleForm key={`account-statement-openingBalance${uploadId}`} item={managerGet('openingBalance')}/>
            </div>, <div key={`account-statement-group2-${uploadId}`} className="d-row gap-15 j-equal">
                <MyLittleForm key={`account-statement-endDate${uploadId}`} item={managerGet('endDate')}/>
                <MyLittleForm key={`account-statement-closingBalance${uploadId}`} item={managerGet('closingBalance')}/>
            </div>
        ], // getSectionErrors             : (rows, mortgage, minimums, section) => {
        //     if (!rows.length) {
        //         return []
        //     }
        //     let sorted = rows.sort(sorterByDateName('documentDate'))
        //     let errors = []
        //     let first = sorted[0]
        //     let last = sorted[sorted.length - 1]
        //     if (dayjs.isDayjs(first.documentDate)) {
        //         let betterEndDate = last.documentDate.add(1,'day')
        //         let {maxAge, requiredRange} = minimums
        //         let [n, rangeUnit] = requiredRange
        //         let [m, ageUnit] = maxAge
        //         // test the age of the most recent document
        //         let age = dayjs().diff(betterEndDate, ageUnit, true)
        //
        //         if (age > m) {
        //             errors.push(`Most recent statement is older than ${m} ${ageUnit} (already in app)`)
        //         }
        //         // then check the range of the documents for
        //         // a) the last doc is not too old - use doc date
        //         // b) the last doc is too old - use today
        //
        //         let requiredFirst = age > m ? dayjs().subtract(n, rangeUnit) : last.documentDate.subtract(n, rangeUnit)
        //         if (!section.record.isNewAccount && first.documentDate.isAfter(requiredFirst)) {
        //             let missingMonthsCount = first.documentDate.diff(requiredFirst, 'months')
        //             if (missingMonthsCount > 0) {
        //                 let missingMonths = []
        //                 missingMonths.push(requiredFirst.format('MMMM'))
        //                 for (let i = 1; i <= missingMonthsCount; i++) {
        //                     let date = requiredFirst.add(i, 'months')
        //                     if (first.documentDate.format('D MM YYYY') !== date.format('D MM YYYY')) {
        //                     missingMonths.push(date.format('MMMM'))
        //                         }
        //                 }
        //                 errors.push(`Missing statements for ${joinWithConjunction(missingMonths, 'and')}`)
        //             }
        //         }
        //     }
        //
        //     let allAnswered = rows.every(it => it.errors.length === 0)
        //     if (!allAnswered) {
        //         errors.push(`Some data are not correct`)
        //     }
        //     return errors
        // },
        getSectionErrors: (rows, mortgage, minimums, section) => {

            if (!rows.length) {
                return []
            }
            let sorted = rows.sort(sorterByDateName('startDate'))
            let errors = []
            let first = sorted[0]
            let last = sorted[sorted.length - 1]
            if (dayjs.isDayjs(first.startDate) && dayjs.isDayjs(last.endDate)) {
                let thisAccount = section.data
                let isNewAccount = thisAccount.isNewAccount
                let isClosed = thisAccount.isClosed
                let betterEndDate = last.endDate.add(1, 'day')
                let {
                    maxAge,
                    requiredRange
                } = minimums
                let [n, rangeUnit] = requiredRange
                let [m, ageUnit] = maxAge
                // Error on endDate too old
                let age = dayjs().diff(betterEndDate, ageUnit, true)
                if (age > m && !isClosed) {
                    errors.push(`Most recent statement is older than ${m} ${ageUnit} (already in app)`)
                }
                //Error on gaps between statements
                let lastDateToWorkWith = age > m ? dayjs().subtract(2, 'week') : last.endDate.add(1, 'day')
                let requiredFirst = lastDateToWorkWith.subtract(n, rangeUnit)

                let rowsWithGapInDaysToNext = sorted.map((it, ix) => {
                    let next = sorted[ix + 1]
                    let daysToNext = 0
                    if (next) {
                        daysToNext = next.startDate.diff(it.endDate, 'days')
                    }
                    return {
                        ...it,
                        daysToNext
                    }
                })
                let missingRanges = []
                if (!isNewAccount) {
                    if (requiredFirst.isBefore(first.startDate)) {
                        missingRanges.push(`[ ${requiredFirst.format('D MMM')} - ${first.startDate.format('D MMM')} ]`)
                    }
                }
                missingRanges = [
                    ...missingRanges, ...rowsWithGapInDaysToNext.flatMap((it, ix) => {
                        if (it.daysToNext > 1) {
                            return [`[ ${it.endDate.add(1, 'day').format('D MMM')} - ${it.endDate.add(it.daysToNext - 1, 'days').format('D MMM')} ]`]
                        }
                        return []
                    })
                ]

                if (missingRanges.length) {
                    errors.push(`Missing statement${missingRanges > 1 ? 's' : ''} for ${joinWithConjunction(missingRanges, 'and')}`)
                }
            }
            return errors
        },
        SectionApplicationForm: ({section}) => {

            let target = section.applicant === 1 ? `applicant1.debts[${section.index}]` : `applicant2.debts[${section.index}]`
            let questions = [
                {
                    name: 'accountNumber',
                    label: 'IBAN',
                    answer: {
                        type: 'text',
                    },
                    target: `${target}.accountNumber`
                }, [
                    {
                        name: 'originalAmount',
                        label: 'Original Loan',
                        answer: {
                            type: 'euro',
                        },
                        target: `${target}.originalAmount`,
                        rules: [{required: false}]
                    }, {
                        name: 'outstanding',
                        label: 'Outstanding',
                        answer: {
                            type: 'euro'
                        },
                        target: `${target}.outstanding`,
                        rules: [{required: false}]
                    }
                ], [
                    {
                        name: 'monthlyPayment',
                        label: 'Monthly Payment',
                        answer: {
                            type: 'euro'
                        },
                        target: `${target}.monthlyPayment`,
                        rules: [{required: false}]
                    }, {
                        name: 'endDate',
                        label: 'Final Payment Date',
                        answer: {
                            type: 'date'
                        },
                        target: `${target}.endDate`,
                        rules: [{required: false}]
                    }
                ], [
                    {
                        name: 'isNewAccount',
                        label: 'Is New Account',
                        answer: {
                            type: 'select',
                            choices: [
                                {
                                    value: true,
                                    text: 'Yes'
                                }, {
                                    value: false,
                                    text: 'No'
                                }
                            ]

                        },
                        target: `${target}.isNewAccount`
                    }, {
                        name: 'isClosed',
                        label: 'Is Closed',
                        answer: {
                            type: 'select',
                            choices: [
                                {
                                    value: true,
                                    text: 'Yes'
                                }, {
                                    value: false,
                                    text: 'No'
                                }
                            ]

                        },
                        target: `${target}.isClosed`
                    },
                ], {
                    name: 'currency',
                    label: 'Currency',
                    answer: {
                        type: 'text'
                    },
                    target: `${target}.currency`,
                    rules: [{required: false}]
                },
            ]
            return <MortgageForm questions={questions}/>
        },
    }
    const mortgageStatementConfig = {
        template: {
            items: [
                {
                    name: 'accountNumber',
                    label: 'Account Number',
                    type: 'text'
                }, {
                    name: 'outstandingBalance',
                    label: 'Balance',
                    type: 'currency',
                }, {
                    name: 'startDate',
                    label: 'Start Date',
                    type: 'date',
                }, {
                    name: 'endDate',
                    label: 'End Date',
                    type: 'date',
                }
            ]
        },
        sortDate: 'startDate',
        uploadsSummaryColumns: [
            {
                title: 'Start Date',
                key: 'startDate',
                dataIndex: 'startDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'End Date',
                key: 'endDate',
                dataIndex: 'endDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Account Number',
                key: 'accountNumber',
                dataIndex: 'accountNumber'
            }, {
                title: 'Outstanding',
                key: 'outstandingBalance',
                dataIndex: 'outstandingBalance',
                align: 'right',
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let thisAccount = section.data
            let rows = uploads.map(it => ({
                key: it.id,
                accountNumber: valueOrNoData(it, 'accountNumber'),
                startDate: valueOrNoData(it, 'startDate'),
                endDate: valueOrNoData(it, 'endDate'),
                outstandingBalance: valueOrNoData(it, 'outstandingBalance'),
                upload: it
            })).map((item, i) => {
                let errors = []

                if (!dayjs.isDayjs(item.startDate)) {
                    errors.push(`Start Date missing`)
                }
                if (!dayjs.isDayjs(item.endDate)) {
                    errors.push(`End Date missing`)
                }
                if (isNaN(item.outstandingBalance)) {
                    errors.push(`Outstanding Balance missing`)
                }
                if (typeof item.accountNumber !== 'string') {
                    errors.push(`Account Number missing`)
                }
                if (!thisAccount.accountNumber || thisAccount.accountNumber !== item.accountNumber) {
                    errors.push(`Account No mismatch`)
                }
                return {
                    ...item,
                    errors
                }
            })
            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getSectionErrors: (rows, mortgage, minimums, section) => {
            if (!rows.length) {
                return []
            }
            let sorted = rows.sort(sorterByDateName('startDate'))
            let errors = []
            let first = sorted[0]
            let last = sorted[sorted.length - 1]
            let thisAccount = section.data
            let isNewAccount = thisAccount.isNewAccount
            let isClosed = thisAccount.isClosed
            if (dayjs.isDayjs(first.startDate) && dayjs.isDayjs(last.endDate)) {
                let betterEndDate = last.endDate.add(1, 'day')
                let {
                    maxAge,
                    requiredRange
                } = minimums
                let [n, rangeUnit] = requiredRange
                let [m, ageUnit] = maxAge
                // Error on endDate too old
                let age = dayjs().diff(betterEndDate, ageUnit, true)
                if (age > m && !isClosed) {
                    errors.push(`Most recent statement is older than ${m} ${ageUnit} (already in app)`)
                }
                //Error on gaps between statements
                let lastDateToWorkWith = age > m ? dayjs().subtract(2, 'week') : last.endDate.add(1, 'day')
                let requiredFirst = lastDateToWorkWith.subtract(n, rangeUnit)

                let rowsWithGapInDaysToNext = sorted.map((it, ix) => {
                    let next = sorted[ix + 1]
                    let daysToNext = 0
                    if (next) {
                        daysToNext = next.startDate.diff(it.endDate, 'days')
                    }
                    return {
                        ...it,
                        daysToNext
                    }
                })
                let missingRanges = []
                if (!isNewAccount) {
                    if (requiredFirst.isBefore(first.startDate)) {
                        missingRanges.push(`[ ${requiredFirst.format('D MMM')} - ${first.startDate.format('D MMM')} ]`)
                    }
                }
                missingRanges = [
                    ...missingRanges, ...rowsWithGapInDaysToNext.flatMap((it, ix) => {
                        if (it.daysToNext > 1) {
                            return [`[ ${it.endDate.add(1, 'day').format('D MMM')} - ${it.endDate.add(it.daysToNext - 1, 'days').format('D MMM')} ]`]
                        }
                        return []
                    })
                ]

                if (missingRanges.length) {
                    errors.push(`Missing statement${missingRanges > 1 ? 's' : ''} for ${joinWithConjunction(missingRanges, 'and')}`)
                }
            }
            return errors
        },
        getRowLabel: (row) => !!row && !!row.documentDate && `${row.documentDate.format(LONGDATE)}`,
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <MyLittleForm key={`loan-statement-accountNumber-${uploadId}`}
                          item={managerGet('accountNumber')}/>,
            <div className={'d-row gap-15 j-equal'} key={`loan-statement-group1-${uploadId}`}>
                <MyLittleForm key={`loan-statement-startDate-${uploadId}`}
                              item={managerGet('startDate')}/>
                <MyLittleForm key={`loan-statement-endDate-${uploadId}`}
                              item={managerGet('endDate')}/>
            </div>, <div className={'d-row gap-15 j-equal'} key={`loan-statement-group2-${uploadId}`}>
                <div/>
                <MyLittleForm key={`loan-statement-outstandingBalance-${uploadId}`}
                              item={managerGet('outstandingBalance')}/>
            </div>,

        ],
        SectionApplicationForm: ({section}) => {
            let target = section.applicant === 1 ? `applicant1.properties[${section.index}]` : `applicant2.properties[${section.index}]`
            let questions = [
                [
                    {
                        name: 'accountNumber',
                        label: 'Account Number',
                        answer: {
                            type: 'text',
                        },
                        target: `${target}.accountNumber`
                    }, {
                    name: 'originalAmount',
                    label: 'Original Loan',
                    answer: {
                        type: 'euro',
                    },
                    target: `${target}.originalAmount`
                }
                ], [
                    {
                        name: 'monthlyRepayments',
                        label: 'Monthly Payment',
                        answer: {
                            type: 'euro'
                        },
                        target: `${target}.monthlyRepayments`
                    }, {
                        name: 'endDate',
                        label: 'Final Payment Date',
                        answer: {
                            type: 'date'
                        },
                        target: `${target}.endDate`,
                        rules: [{required: false}]
                    }
                ], [
                    {
                        name: 'isNewAccount',
                        label: 'Is New Account',
                        answer: {
                            type: 'select',
                            choices: [
                                {
                                    value: true,
                                    text: 'Yes'
                                }, {
                                    value: false,
                                    text: 'No'
                                }
                            ]
                        },
                        target: `${target}.isNewAccount`
                    }, {
                        name: 'isClosed',
                        label: 'Is Closed',
                        answer: {
                            type: 'select',
                            choices: [
                                {
                                    value: true,
                                    text: 'Yes'
                                }, {
                                    value: false,
                                    text: 'No'
                                }
                            ]
                        },
                        target: `${target}.isClosed`
                    }
                ]
            ]
            return <MortgageForm questions={questions}/>
        },
    }
    const proofOfAddressConfig = {
        template: {
            items: [
                {
                    name: 'documentDate',
                    label: 'Document Date',
                    type: 'date',
                }, {
                    name: 'address',
                    label: 'Address',
                    type: 'text'
                }
            ]
        },
        sortDate: 'documentDate',
        uploadsSummaryColumns: [
            {
                title: 'Date',
                key: 'documentDate',
                dataIndex: 'documentDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Address',
                key: 'address',
                dataIndex: 'address'
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let applicant = section.applicant === 1 ? mortgage.applicant1 : mortgage.applicant2
            let rows = uploads.map(it => ({
                key: it.id,
                address: valueOrNoData(it, 'address'),
                documentDate: valueOrNoData(it, 'documentDate'),
                upload: it
            })).map((item, i) => {
                let errors = []
                if (!dayjs.isDayjs(item.documentDate)) {
                    errors.push(`Doc Date missing`)
                }
                if (compressString(item.address) !== compressString(applicant.personal.addressCurrent)) {
                    errors.push(<>Address does not match: <DifferenceHighlighter str2={item.address}
                                                                                 str1={applicant.personal.addressCurrent}/> </>)
                }
                if (section.applicant === 2 && mortgage.requirement.cohabiting) {
                    if (compressString(item.address) !== compressString(mortgage.applicant1.personal.addressCurrent)) {
                        errors.push(<>Mortgage Setup has them Cohabiting, but address does not match Applicant
                            1: <DifferenceHighlighter str2={item.address}
                                                      str1={mortgage.applicant1.personal.addressCurrent}/> </>)
                    }
                }
                if (typeof item.address !== 'string') {
                    errors.push(`Address missing`)
                }

                return {
                    ...item,
                    errors
                }
            })
            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getSectionErrors: (rows, mortgage, minimums, section) => {
            if (!rows.length) {
                return []
            }
            let applicant = section.applicant === 1 ? mortgage.applicant1 : mortgage.applicant2
            let sorted = rows.sort(sorterByDateName('documentDate'))
            let last = sorted[sorted.length - 1]
            let errors = []
            let noneStamped = !last.upload.stampedOn
            if (noneStamped) {
                errors.push(`Document not stamped`)
            }
            let additionalQuestions = []
            let today = dayjs()
            if (dayjs.isDayjs(last.documentDate)) {
                let mths = today.diff(last.documentDate, 'months', true)
                if (mths > 3) {
                    errors.push(`Over three months old`)
                }
            }

            if (typeof last.address === 'string') {
                if (compressString(last.address) !== compressString(applicant.personal.addressCurrent)) {
                    errors.push('Address does not match')
                    additionalQuestions.push({
                        name: 'currentAddress',
                        label: 'Current Address',
                        answer: {
                            type: 'text'
                        },
                        target: `applicant${section.applicant}.personal.addressCurrent`
                    })
                }
            }
            if (additionalQuestions.length) {
                return {
                    errors,
                    additionalQuestions
                }
            }

            return errors
        },
        getRowLabel: (row) => !!row && !!row.documentDate && `${row.documentDate.format(LONGDATE)}`,
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <MyLittleForm key={`poa-documentDate-${uploadId}`} item={managerGet('documentDate')}/>,
            <MyLittleForm key={`poa-address-${uploadId}`} item={managerGet('address')}/>
        ],
        SectionApplicationForm: ({
                                     section,
                                     additionalQuestions
                                 }) => {
            if (!additionalQuestions || !additionalQuestions.length) {
                return null
            }
            return <MortgageForm questions={additionalQuestions}/>
        }
    }
    const payslipConfig = {
        template: {
            items: [
                {
                    name: 'documentDate',
                    label: 'Payslip Date',
                    type: 'date',
                }, {
                    name: 'grossBasic',
                    label: 'Gross Basic',
                    type: 'currency',

                }, {
                    name: 'netPay',
                    label: 'Net Pay',
                    type: 'currency',
                }, {
                    name: 'grossPay',
                    label: 'Gross Pay',
                    type: 'currency',
                }, {
                    name: 'pensionDeduction',
                    label: 'Pension Deduction',
                    type: 'currency',
                    optional: true,
                }, {
                    name: 'avcDeduction',
                    label: 'AVC Deduction',
                    type: 'currency',
                    optional: true,
                }
            ]
        },
        sortDate: 'documentDate',
        uploadsSummaryColumns: [
            {
                title: 'Date',
                key: 'documentDate',
                dataIndex: 'documentDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Gross Basic',
                key: 'grossBasic',
                dataIndex: 'grossBasic',
                align: 'right'
            }, {
                title: 'Net Pay',
                key: 'netPay',
                dataIndex: 'netPay',
                align: 'right',
            }, {
                title: 'Gross Pay',
                key: 'grossPay',
                dataIndex: 'grossPay',
                align: 'right',
            }, {
                title: 'Pension',
                key: 'pensionDeduction',
                dataIndex: 'pensionDeduction',
                align: 'right',
            }, {
                title: 'AVC',
                key: 'avcDeduction',
                dataIndex: 'avcDeduction',
                align: 'right',
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>//text.join(', ')
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {

            let employment = section.data
            let freq = employment?.payFrequency || 'MONTHLY'

            let rows = uploads.map(it => ({
                key: it.id,
                netPay: valueOrNoData(it, 'netPay'),
                grossBasic: valueOrNoData(it, 'grossBasic'),
                grossPay: valueOrNoData(it, 'grossPay'),
                documentDate: valueOrNoData(it, 'documentDate'),
                pensionDeduction: valueOrNoData(it, 'pensionDeduction'),
                avcDeduction: valueOrNoData(it, 'avcDeduction'),
                upload: it
            })).sort(sorterByDateName('documentDate'))
            let numberRequired = 0
            switch (freq) {
                case Frequencies.MONTHLY:
                    numberRequired = 3;
                    break;
                case Frequencies.FORTNIGHTLY:
                    numberRequired = 5;
                    break;
                case Frequencies.WEEKLY:
                    numberRequired = 10;
                    break;
            }
            rows = rows.map((item, i) => {
                let errors = []
                if (!dayjs.isDayjs(item.documentDate)) {
                    errors.push(`Doc Date missing`)
                }
                if (isNaN(item.netPay)) {
                    errors.push(`Net Pay missing`)
                }
                if (isNaN(item.grossPay)) {
                    errors.push(`Gross Pay missing`)
                }
                if (isNaN(item.grossBasic)) {
                    errors.push(`Gross Basic missing`)
                }
                if (rows.length > 1) {
                    let nextDate = rows[i + 1]?.documentDate
                    if (freq && nextDate && dayjs.isDayjs(nextDate) && dayjs.isDayjs(item.documentDate)) {
                        let diff = nextDate.diff(item.documentDate, "days")
                        let maxDiff
                        switch (freq) {
                            case Frequencies.MONTHLY:
                                maxDiff = 35;
                                break
                            case Frequencies.FORTNIGHTLY:
                                maxDiff = 18;
                                break
                            case Frequencies.WEEKLY:
                                maxDiff = 10;
                                break;
                        }
                        if (diff > maxDiff) {
                            errors.push(`Missing payslip after`)
                        }
                    }
                }
                return {
                    ...item,
                    errors
                }

            })
            let requiredFirst = getFirstRequiredDate(section, rows)

            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getRowLabel: (row) => !!row && dayjs.isDayjs(row.documentDate) && `${row.documentDate.format(LONGDATE)}`,
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <div className="d-row j-equal" key={`payslip-group-1-${uploadId}`}>
                <MyLittleForm key={`payslip-documentDate-${uploadId}`} item={managerGet('documentDate')}/>,
                <MyLittleForm key={`payslip-grossBasic-${uploadId}`} item={managerGet('grossBasic')}/>
            </div>, <div className="d-row j-equal" key={`payslip-group-1-${uploadId}`}>
                <MyLittleForm key={`payslip-grossPay-${uploadId}`} item={managerGet('grossPay')}/>
                <MyLittleForm key={`payslip-netPay-${uploadId}`} item={managerGet('netPay')}/>
            </div>, <div className="d-row j-equal" key={`payslip-group-2-${uploadId}`}>
                <MyLittleForm key={`payslip-pensionDeduction-${uploadId}`} item={managerGet('pensionDeduction')}/>
                <MyLittleForm key={`payslip-avcDeduction-${uploadId}`} item={managerGet('avcDeduction')}/>
            </div>
        ],
        getSectionErrors: (rows, mortgage, minimums, section) => {

            if (!rows.length) {
                return []
            }
            if (rows.some(row => row?.errors?.length)) {
                return []
            }
            let sorted = rows.sort(sorterByDateName('documentDate'))

            let errors = []
            let first = sorted[0]
            let last = sorted[sorted.length - 1]
            if (!section.data.payFrequency) {
                let oldIncome = section.applicant === 1 ? mortgage.applicant1.income : mortgage.applicant2.income
                if (!oldIncome.payFrequency) {
                    errors.push('Pay Frequency missing')
                } else {
                    section.data.payFrequency = oldIncome.payFrequency
                }
            }

            let errorAllowance
            if (section.data.payFrequency) {
                let increment
                let minRange
                switch (section.data.payFrequency) {
                    case Frequencies.MONTHLY:
                        increment = [
                            1, 'month'
                        ]
                        minRange = 3
                        errorAllowance = 14 // can be 14 days off
                        break;
                    case Frequencies.FORTNIGHTLY:
                        increment = [
                            2, 'weeks'
                        ]
                        errorAllowance = 10 // can be 10 days off
                        minRange = 5
                        break;
                    case Frequencies.WEEKLY:
                        increment = [
                            1, 'week'
                        ]
                        errorAllowance = 4 // can be 4 days off
                        minRange = 10
                        break;
                    default:
                }

                // get the expected payslip timeframe;
                let today = dayjs()
                let noPayslipsNeededAfterDate = today.subtract(1, 'month').endOf('month').endOf('day')


                // add missing paylsips After last
                let expectedPayslipDatesAfter = []
                if (dayjs.isDayjs(last.documentDate) && last.documentDate.isBefore(noPayslipsNeededAfterDate)) {
                    let currTestDate = last.documentDate.clone().add(...increment)
                    while (currTestDate.isBefore(noPayslipsNeededAfterDate)) {
                        expectedPayslipDatesAfter.push(currTestDate)
                        currTestDate = currTestDate.add(...increment)
                    }
                }
                let lastRequiredDate = expectedPayslipDatesAfter.length ? expectedPayslipDatesAfter[expectedPayslipDatesAfter.length - 1] : last.documentDate
                let noPayslipsNeededBeforeDate = lastRequiredDate.subtract(3, 'months')
                let expectedPayslipDatesBefore = []
                let currentDate = lastRequiredDate.clone()
                while (currentDate.isAfter(noPayslipsNeededBeforeDate)) {
                    expectedPayslipDatesBefore.push(currentDate)
                    currentDate = currentDate.subtract(...increment)
                }


                let missingPayslips = expectedPayslipDatesBefore.filter(date => !sorted.some(it => dayjs.isDayjs(it.documentDate) && Math.abs(it.documentDate.diff(date, 'days')) <= errorAllowance));
                if (missingPayslips.length) {
                    errors.push(`Missing payslip${missingPayslips.length > 1 ? 's' : ''} for ${joinWithConjunction(missingPayslips.reverse().map(it => it.format('D MMM')), 'and')}`)
                }

                // set standard most recent ot of date

                if (last.documentDate) {
                    let betterEndDate = last.documentDate.add(1, 'day')
                    let {
                        maxAge,
                        requiredRange
                    } = minimums
                    let [m, ageUnit] = maxAge
                    let age = dayjs().diff(betterEndDate, ageUnit, true)
                    if (age > m) {
                        errors.push(`Most recent payslip is older than ${m} ${ageUnit} (already in app)`)
                    }
                }

                // if (dayjs.isDayjs(last.documentDate)) {
                //
                //     // find possible dates after the last payslip
                //     let possibleDatesPostLast = []
                //
                //     let lastTestDate = last.documentDate.add(...increment)
                //     while (lastTestDate.isBefore(today)) {
                //         possibleDatesPostLast.push(lastTestDate)
                //         lastTestDate = lastTestDate.add(...increment)
                //     }
                //     let practicalLastDate = possibleDatesPostLast.length ? possibleDatesPostLast[possibleDatesPostLast.length - 1] : last.documentDate
                //
                //     // if there are no more payslips possible in this month, include the current month
                //
                //     let nextAfterLast = practicalLastDate.add(...increment)
                //
                //     let includeCurrentMonth = practicalLastDate.isBefore(today)
                //     // set the required months to the previous 3, unless no more payslips are possible in this month
                //     let monthsToInclude = includeCurrentMonth ? [
                //         0,
                //         1,
                //         2
                //     ] : [
                //         1,
                //         2,
                //         3
                //     ]
                //     let requiredMonths = monthsToInclude.map(n => {
                //         return today.subtract(n, 'month').format('MMM')
                //     })
                //     let requiredPayslips = []
                //     let testDate = practicalLastDate

                //     while (requiredMonths.includes(testDate.format('MMM'))) {

                //
                //         }
                //
                //         requiredPayslips.push(testDate)
                //         testDate = testDate.subtract(...increment)
                //     }
                //     if (section.applicant ===1) {

                //     }
                //     let missingPayslips = requiredPayslips.filter(date => !sorted.some(it => dayjs.isDayjs(it.documentDate) && Math.abs(it.documentDate.diff(date, 'days')) <= 5));
                //
                //     if (missingPayslips.length) {
                //         errors.push(`Missing payslip${missingPayslips.length > 1 ? 's' : ''} for ${joinWithConjunction(missingPayslips.reverse().map(it => it.format('D MMM')), 'and')}`)
                //     }
                //
                //
                //
                //
                //
                // }
            }

            return errors
        },
        SectionApplicationForm: ({section}) => {

            let target = !!section.index ? section.applicant === 1 ? `applicant1.employmentIncome[${section.index}]` : `applicant2.employmentIncome[${section.index}]` : section.applicant === 1 ? `applicant1.income` : `applicant2.income`

            let questions = [
                {
                    name: 'payFrequency',
                    label: 'Pay Frequency',
                    answer: {
                        type: 'select',
                        choices: [
                            Frequencies.WEEKLY, Frequencies.MONTHLY, Frequencies.FORTNIGHTLY
                        ].map(it => ({
                            value: it,
                            label: capitaliseFirst(it.toLowerCase())
                        }))
                    },
                    target: `${target}.payFrequency`
                }
            ]

            return <MortgageForm questions={questions}/>
        }
    }
    const edsConfig = {
        template: {
            items: [
                {
                    name: 'year',
                    label: 'Year',
                    type: 'text',
                }, {
                    name: 'grossBasic',
                    label: 'Gross Basic',
                    type: 'currency',
                    optional: true,
                }, {
                    name: 'grossBasic1',
                    label: 'Gross Basic 2',
                    type: 'currency',
                    optional: true,
                }, {
                    name: 'grossBasic2',
                    label: 'Gross Basic 3',
                    type: 'currency',
                    optional: true,
                }, {
                    name: 'employer1',
                    label: 'Employer 1',
                    type: 'text',
                    optional: true,
                }, {
                    name: 'pay1',
                    label: 'Pay 1',
                    type: 'currency',
                }, {
                    name: 'start1',
                    label: 'Start 1',
                    type: 'date',
                }, {
                    name: 'end1',
                    label: 'End 1',
                    type: 'date',
                    optional: true,
                }, {
                    name: 'employer2',
                    label: 'Employer 2',
                    type: 'text',
                    optional: true,
                }, {
                    name: 'pay2',
                    label: 'Pay 2',
                    type: 'currency',
                    optional: true,
                }, {
                    name: 'start2',
                    label: 'Start 2',
                    type: 'date',
                    optional: true,
                }, {
                    name: 'end2',
                    label: 'End 2',
                    type: 'date',
                    optional: true,
                }, {
                    name: 'employer3',
                    label: 'Employer 3',
                    type: 'text',
                    optional: true,
                }, {
                    name: 'pay3',
                    label: 'Pay 3',
                    type: 'currency',
                    optional: true,
                }, {
                    name: 'start3',
                    label: 'Start 3',
                    type: 'date',
                    optional: true,
                }, {
                    name: 'end3',
                    label: 'End 3',
                    type: 'date',
                    optional: true,
                }
            ]
        },
        sortDate: 'fakeYearDate',
        uploadsSummaryColumns: [
            {
                title: 'Year',
                key: 'year',
                dataIndex: 'year',
            }, {
                title: 'Gross Basic',
                key: 'grossBasic',
                dataIndex: 'grossBasic',
                align: 'right',
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let rows = uploads.map(it => {
                //let year = valueOrNoData(it, 'year')
                // let gb = valueOrNoData(it, 'grossBasic')
                // let gb1 = valueOrNoData(it, 'grossBasic1')
                // let gb2 = valueOrNoData(it, 'grossBasic2')
                let pay1 = valueOrNoData(it, 'pay1')
                let pay2 = valueOrNoData(it, 'pay2')
                let pay3 = valueOrNoData(it, 'pay3')

                let sum = 0
                if (!isNaN(pay1)) {
                    sum += Number(pay1)
                }
                if (!isNaN(pay2)) {
                    sum += Number(pay2)
                }
                if (!isNaN(pay3)) {
                    sum += Number(pay3)
                }

                return {
                    key: it.id,
                    fakeYearDate: dayjs(valueOrNoData(it, 'year'), 'YYYY'),
                    year: valueOrNoData(it, 'year'),
                    grossBasic: sum > 0 ? Number(sum.toFixed(2)) : <span style={{color: red.primary}}>No Data</span>,
                    upload: it
                }
            }).sort(sorterByDateName('fakeYearDate'))

            rows = rows.map((item, i) => {
                let errors = []
                if (typeof item.year !== 'string') {
                    errors.push(`Year missing`)
                }
                if (isNaN(item.grossBasic)) {
                    errors.push(`Gross Basic missing`)
                }
                return {
                    ...item,
                    errors
                }
            })
            return rows
        },
        getRowLabel: (row) => !!row && !!row.year && `${row.year}`,
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <div className="d-col gap-3">
                <MyLittleForm key={`eds-year-${uploadId}`} item={managerGet('year')}/>
                <div className="w-100" style={{borderBottom: '1px solid #a3a3a3'}}/>
                <div className="d-row j-equal">
                    <MyLittleForm key={`eds-employer1-${uploadId}`} item={managerGet('employer1')}/>
                    <MyLittleForm key={`eds-start1-${uploadId}`} item={managerGet('start1')}/>

                </div>
                <div className="d-row j-equal">
                    <MyLittleForm key={`eds-pay1-${uploadId}`} item={managerGet('pay1')}/>
                    <MyLittleForm key={`eds-end1-${uploadId}`} item={managerGet('end1')}/>
                </div>
                <div className="w-100" style={{borderBottom: '1px solid #a3a3a3'}}/>
                <div className="d-row j-equal">
                    <MyLittleForm key={`eds-employer2-${uploadId}`} item={managerGet('employer2')}/>
                    <MyLittleForm key={`eds-start2-${uploadId}`} item={managerGet('start2')}/>

                </div>
                <div className="d-row j-equal">
                    <MyLittleForm key={`eds-pay2-${uploadId}`} item={managerGet('pay2')}/>
                    <MyLittleForm key={`eds-end2-${uploadId}`} item={managerGet('end2')}/>
                </div>
                <div className="w-100" style={{borderBottom: '1px solid #a3a3a3'}}/>
                <div className="d-row j-equal">
                    <MyLittleForm key={`eds-employer3-${uploadId}`} item={managerGet('employer3')}/>
                    <MyLittleForm key={`eds-start3-${uploadId}`} item={managerGet('start3')}/>
                </div>
                <div className="d-row j-equal">
                    <MyLittleForm key={`eds-pay3-${uploadId}`} item={managerGet('pay3')}/>
                    <MyLittleForm key={`eds-end3-${uploadId}`} item={managerGet('end3')}/>
                </div>
            </div>
            // <div className="d-row j-equal" key={`eds-group1-${uploadId}`}>
            //     <MyLittleForm key={`eds-year-${uploadId}`} item={managerGet('year')}/>
            //     <MyLittleForm key={`eds-grossBasic-${uploadId}`} item={managerGet('grossBasic')}/>
            // </div>,
            // <div className="d-row j-equal" key={`eds-group3-${uploadId}`}>
            //     <div/>
            //     <MyLittleForm key={`eds-grossBasic1-${uploadId}`} item={managerGet('grossBasic1')}/>
            // </div>,
            // <div className="d-row j-equal" key={`eds-group4-${uploadId}`}>
            //     <div/>
            //     <MyLittleForm key={`eds-grossBasic2-${uploadId}`} item={managerGet('grossBasic2')}/>
            // </div>,

        ],
        getSectionErrors: (rows, mortgage, minimums, section) => {
            if (!rows.length) {
                return []
            }
            let sorted = rows.sort(sorterByDateName('fakeYearDate'))

            let errors = []
            let first = sorted[0]
            let last = sorted[sorted.length - 1]

            if (dayjs.isDayjs(first.fakeYearDate)) {

                let betterEndDate = last.fakeYearDate

                let {
                    maxAge,
                    requiredRange
                } = minimums
                let [n, rangeUnit] = requiredRange
                let [m, ageUnit] = maxAge
                // test the age of the most recent document
                let age = dayjs().diff(betterEndDate, ageUnit, true)

                if (age > m) {
                    errors.push(`Most recent EDS is too old (already in app)`)
                }
                // then check the range of the documents for
                // a) the last doc is not too old - use doc date
                // b) the last doc is too old - use today

                let required = [
                    lastYear1, lastYear2, lastYear3
                ]

                required.forEach((year, i) => {
                    let found = rows.find(it => Number(it.fakeYearDate.format("YYYY")) === year)
                    if (!found) {
                        errors.push(`Missing EDS for ${year}`)
                    }
                })
            }
            return errors
        },
        SectionApplicationForm: ({
                                     section,
                                     mortgage
                                 }) => {
            let target = section.applicant === 1 ? `applicant1` : `applicant2`
            let legacy = legacyIncome(mortgage)
            let target2 = legacy ? `${target}.employment` : `${target}.employmentIncome[${section.applicant === 1 ? mortgage.applicant1.employmentIncome[0].id : mortgage.applicant2.employmentIncome[0].id}]`
            let questions = [
                {
                    name: 'ppsNumber',
                    label: 'PPS Number',
                    answer: {
                        type: 'text',
                    },
                    target: `${target}.tax.ppsNumber`
                }, {
                    name: 'currentEmployerName',
                    label: 'Current Employer Name',
                    answer: {
                        type: 'text',
                    },
                    target: `${target2}.currentEmployerName`
                }, {
                    name: 'currentEmployerStartDate',
                    label: 'Start Date',
                    answer: {
                        type: 'date',
                    },
                    target: `${target2}.currentEmployerStartDate`
                }, {
                    name: 'previousEmployerName',
                    label: 'Previous Employer Name',
                    answer: {
                        type: 'text',
                    },
                    hides: (form) => {
                        let day = form.getFieldValue('currentEmployerStartDate')
                        if (day && dayjs.isDayjs(day)) {
                            return dayjs().diff(day, 'years') >= 3
                        }
                        return true
                    },
                    target: `${target2}.previousEmployerName`
                }, {
                    name: 'previousEmployerStartDate',
                    label: 'Start Date',
                    answer: {
                        type: 'date',
                    },
                    hides: (form) => {
                        let day = form.getFieldValue('currentEmployerStartDate')
                        if (day && dayjs.isDayjs(day)) {
                            return dayjs().diff(day, 'years') >= 3
                        }
                        return true
                    },
                    target: `${target2}.previousEmployerStartDate`
                }, {
                    name: 'previousEmployerEndDate',
                    label: 'End Date',
                    answer: {
                        type: 'date',
                    },
                    hides: (form) => {
                        let day = form.getFieldValue('currentEmployerStartDate')
                        if (day && dayjs.isDayjs(day)) {
                            return dayjs().diff(day, 'years') >= 3
                        }
                        return true
                    },
                    target: `${target2}.previousEmployerEndDate`
                }
            ]
            return <MortgageForm questions={questions}/>
        }
    }
    const salaryCertConfig = {
        template: {
            items: [
                {
                    name: 'grossBasicThisYear',
                    label: 'Gross Basic This Year',
                    type: 'currency',
                },
                {
                    name: 'grossBasicYearMostRecent',
                    label: 'Gross Basic Year Most Recent',
                    type: 'currency',
                },
                {
                    name: 'documentDate',
                    label: 'Document Date',
                    type: 'date',
                }, {
                    name: 'grossBasic',
                    label: 'Gross Basic',
                    type: 'currency',
                }, {
                    name: 'grossBasicYearBefore',
                    label: 'Gross Basic Last Year',
                    type: 'currency',
                }, {
                    name: 'grossBasicYearBeforeThat',
                    label: 'Gross Basic Last Year',
                    type: 'currency',
                }, {
                    name: 'overtime',
                    label: 'Overtime',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'commission',
                    label: 'Commission',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'commissionGuaranteed',
                    label: 'Guaranteed',
                    type: 'check',
                    optional: true
                }, {
                    name: 'allowances',
                    label: 'Allowances',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'allowancesGuaranteed',
                    label: 'Guaranteed',
                    type: 'check',
                    optional: true
                }, //shift allowance
                {
                    name: 'shiftAllowanceYearMostRecent',
                    label: 'shiftAllowanceYearMostRecent',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'shiftAllowanceYearBefore',
                    label: 'shiftAllowanceYearBefore',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'shiftAllowanceYearBeforeThat',
                    label: 'shiftAllowanceYearBeforeThat',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'shiftAllowanceGuaranteed',
                    label: 'shiftAllowanceGuaranteed',
                    type: 'check',
                    optional: true
                }, // car allowance
                {
                    name: 'carAllowanceYearMostRecent',
                    label: 'carAllowanceYearMostRecent',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'carAllowanceYearBefore',
                    label: 'carAllowanceYearBefore',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'carAllowanceYearBeforeThat',
                    label: 'carAllowanceYearBeforeThat',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'carAllowanceGuaranteed',
                    label: 'carAllowanceGuaranteed',
                    type: 'check',
                    optional: true
                }, //other allowance
                {
                    name: 'otherAllowanceYearMostRecent',
                    label: 'Other Allowance Year Most Recent',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'otherAllowanceYearBefore',
                    label: 'Other Allowance Year Before',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'otherAllowanceYearBeforeThat',
                    label: 'otherAllowanceYearBeforeThat',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'otherAllowanceGuaranteed',
                    label: 'otherAllowanceGuaranteed',
                    type: 'check',
                    optional: true
                }, //overtime
                {
                    name: 'overtimeYearMostRecent',
                    label: 'overtime Year Most Recent',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'overtimeYearBefore',
                    label: 'overtime Year Before',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'overtimeYearBeforeThat',
                    label: 'overtimeYearBeforeThat',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'overtimeGuaranteed',
                    label: 'overtimeGuaranteed',
                    type: 'check',
                    optional: true
                }, //bonus
                {
                    name: 'bonusYearMostRecent',
                    label: 'bonus Year Most Recent',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'bonusYearBefore',
                    label: 'bonus Year Before',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'bonusYearBeforeThat',
                    label: 'bonusYearBeforeThat',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'bonusGuaranteed',
                    label: 'bonusGuaranteed',
                    type: 'check',
                    optional: true
                }, // commission
                {
                    name: 'commissionYearMostRecent',
                    label: 'commission Year Most Recent',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'commissionYearBefore',
                    label: 'commission Year Before',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'commissionYearBeforeThat',
                    label: 'commissionYearBeforeThat',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'commissionGuaranteed',
                    label: 'commissionGuaranteed',
                    type: 'check',
                    optional: true
                },
                //TODO : add in next 3 payscales
                {
                    name: 'other',
                    label: 'Other',
                    type: 'currency',
                    optional: true
                }, {
                    name: 'otherGuaranteed',
                    label: 'Guaranteed',
                    type: 'check',
                    optional: true
                }, ...(() => {
                    return [
                        lastYear1, lastYear2, lastYear3
                    ].flatMap(year => {
                        if (year === lastYear1) {
                            return [
                                {
                                    name: `${year}bonus`,
                                    label: `Bonus ${year}`,
                                    type: 'currency',
                                    optional: true
                                }, {
                                    name: `${year}bonusGuaranteed`,
                                    label: `Guaranteed`,
                                    type: 'check',
                                    optional: true
                                }
                            ]
                        }
                        return [
                            {
                                name: `${year}bonus`,
                                label: `Bonus ${year}`,
                                type: 'currency',
                                optional: true
                            }
                        ]
                    })
                })()
            ]
        },
        sortDate: 'documentDate',
        uploadsSummaryColumns: [
            {
                title: 'Date',
                key: 'documentDate',
                dataIndex: 'documentDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Gross Basic',
                key: 'grossBasic',
                dataIndex: 'grossBasic',
                align: 'right',
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let rows = uploads.map(it => ({
                key: it.id,
                grossBasic: valueOrNoData(it, 'grossBasic'),
                documentDate: valueOrNoData(it, 'documentDate'),
                upload: it
            })).sort(sorterByDateName('documentDate'))
            rows = rows.map((item, i) => {
                let errors = []
                if (!dayjs.isDayjs(item.documentDate)) {
                    errors.push(`Doc Date missing`)
                }
                if (isNaN(item.grossBasic)) {
                    errors.push(`Gross Basic missing`)
                }
                return {
                    ...item,
                    errors
                }
            })
            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getRowLabel: (row) => !!row && !!row.documentDate && dayjs.isDayjs(row.documentDate) && row.documentDate.format(LONGDATE),
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <>
                <MyLittleForm key={`salary-cert-documentDate-${uploadId}`} item={managerGet('documentDate')} showLabel={true} inExtWindow={false}/>
                <table>

                    <tbody>
                    <tr>
                        <td>

                        </td>

                        <td className="text-end">
                            Last Year
                        </td>
                        <td className="text-end">
                            Year Before
                        </td>
                        <td className="text-end">
                            Year Before That
                        </td>
                        <td className="text-end">
                            Guaranteed
                        </td>
                    </tr>
                    <tr>

                        <td>
                            Gross annual basic
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-grossBasic-${uploadId}`} item={managerGet('grossBasic')}
                                          showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-grossBasicYearBefore-${uploadId}`}
                                          item={managerGet('grossBasicYearBefore')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-grossBasicYearBeforeThat-${uploadId}`}
                                          item={managerGet('grossBasicYearBeforeThat')} showLabel={false}/>
                        </td>
                        <td>

                        </td>
                    </tr>
                    <tr>
                        <td>
                            Car allowance
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cart-carAllowanceYearMostRecent-${uploadId}`}
                                          item={managerGet('carAllowanceYearMostRecent')}
                                          showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-carAllowanceYearBefore-${uploadId}`}
                                          item={managerGet('carAllowanceYearBefore')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-carAllowanceYearBeforeThat-${uploadId}`}
                                          item={managerGet('carAllowanceYearBeforeThat')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-carAllowanceGuaranteed-${uploadId}`}
                                          item={managerGet('carAllowanceGuaranteed')}
                                          showLabel={false}/>
                        </td>

                    </tr>
                    <tr>
                        <td>
                            Shift allowance
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cart-shiftAllowanceYearMostRecent-${uploadId}`}
                                          item={managerGet('shiftAllowanceYearMostRecent')}
                                          showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-shiftAllowanceYearBefore-${uploadId}`}
                                          item={managerGet('shiftAllowanceYearBefore')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-shiftAllowanceYearBeforeThat-${uploadId}`}
                                          item={managerGet('shiftAllowanceYearBeforeThat')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-shiftAllowanceGuaranteed-${uploadId}`}
                                          item={managerGet('shiftAllowanceGuaranteed')}
                                          showLabel={false}/>
                        </td>

                    </tr>
                    <tr>
                        <td>
                            Other allowance
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cart-otherAllowanceYearMostRecent-${uploadId}`}
                                          item={managerGet('otherAllowanceYearMostRecent')}
                                          showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-otherAllowanceYearBefore-${uploadId}`}
                                          item={managerGet('otherAllowanceYearBefore')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-otherAllowanceYearBeforeThat-${uploadId}`}
                                          item={managerGet('otherAllowanceYearBeforeThat')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-otherAllowanceGuaranteed-${uploadId}`}
                                          item={managerGet('otherAllowanceGuaranteed')}
                                          showLabel={false}/>
                        </td>

                    </tr>
                    <tr>
                        <td>
                            Overtime
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cart-overtimeYearMostRecent-${uploadId}`}
                                          item={managerGet('overtimeYearMostRecent')}
                                          showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-overtimeYearBefore-${uploadId}`}
                                          item={managerGet('overtimeYearBefore')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-overtimeYearBeforeThat-${uploadId}`}
                                          item={managerGet('overtimeYearBeforeThat')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-overtimeGuaranteed-${uploadId}`}
                                          item={managerGet('overtimeGuaranteed')}
                                          showLabel={false}/>
                        </td>

                    </tr>
                    <tr>
                        <td>
                            Commission
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cart-commissionYearMostRecent-${uploadId}`}
                                          item={managerGet('commissionYearMostRecent')}
                                          showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-commissionYearBefore-${uploadId}`}
                                          item={managerGet('commissionYearBefore')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-commissionYearBeforeThat-${uploadId}`}
                                          item={managerGet('commissionYearBeforeThat')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-commissionGuaranteed-${uploadId}`}
                                          item={managerGet('overtimeGuaranteed')}
                                          showLabel={false}/>
                        </td>

                    </tr>
                    <tr>
                        <td>
                            Bonus
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cart-bonusYearMostRecent-${uploadId}`}
                                          item={managerGet('bonusYearMostRecent')}
                                          showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-bonusYearBefore-${uploadId}`}
                                          item={managerGet('bonusYearBefore')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-bonusYearBeforeThat-${uploadId}`}
                                          item={managerGet('bonusYearBeforeThat')} showLabel={false}/>
                        </td>
                        <td>
                            <MyLittleForm key={`salary-cert-bonusGuaranteed-${uploadId}`}
                                          item={managerGet('bonusGuaranteed')}
                                          showLabel={false}/>
                        </td>

                    </tr>
                    </tbody>
                </table>

                <div className={'d-row j-equal'} key={`salary-cert-group1-${uploadId}`}>
                    {/*<MyLittleForm key={`salary-cert-documentDate1-${uploadId}`} item={managerGet('documentDate')}/>*/}
                    {/*<MyLittleForm key={`salary-cert-grossBasic-${uploadId}`} item={managerGet('grossBasic')}/>*/}
                </div>
                <div className={'d-row j-equal'} key={`salary-cert-group2-${uploadId}`}>
                    {/*<MyLittleForm key={`salary-cert-overtime-${uploadId}`} item={managerGet('overtime')}/>*/}
                    {/*<MyLittleForm key={`salary-cert-overtimeGuaranteed-${uploadId}`}*/}
                    {/*			  item={managerGet('overtimeGuaranteed')}/>*/}
                </div>
                , <div className={'d-row j-equal'} key={`salary-cert-group3-${uploadId}`}>
                <MyLittleForm key={`salary-cert-commission-${uploadId}`} item={managerGet('commission')}/>
                <MyLittleForm key={`salary-cert-commissionGuaranteed-${uploadId}`}
                              item={managerGet('commissionGuaranteed')}/>
            </div>
                <div className={'d-row j-equal'} key={`salary-cert-group4-${uploadId}`}>
                    <MyLittleForm key={`salary-cert-allowances-${uploadId}`} item={managerGet('allowances')}/>
                    <MyLittleForm key={`salary-cert-allowancesGuaranteed-${uploadId}`}
                                  item={managerGet('allowancesGuaranteed')}/>
                </div>
                <div className={'d-row j-equal'} key={`salary-cert-group5-${uploadId}`}>
                    <MyLittleForm key={`salary-cert-other-${uploadId}`} item={managerGet('other')}/>
                    <MyLittleForm key={`salary-cert-otherGuaranteed1-${uploadId}`}
                                  item={managerGet('otherGuaranteed')}/>
                </div>
                <div className={'d-row j-equal'} key={`salary-cert-group6-${uploadId}`}>
                    <MyLittleForm key={`salary-cert-${lastYear1}bonus-${uploadId}`}
                                  item={managerGet(`${lastYear1}bonus`)}/>
                    <MyLittleForm key={`salary-cert-${lastYear1}bonusGuaranteed\`-${uploadId}`}
                                  item={managerGet(`${lastYear1}bonusGuaranteed`)}/>
                </div>
                <div className={'d-row j-equal'} key={`salary-cert-group7-${uploadId}`}>
                    <MyLittleForm key={`salary-cert-${lastYear2}bonus-${uploadId}`}
                                  item={managerGet(`${lastYear2}bonus`)}/>
                    <div/>
                </div>
                <div className={'d-row j-equal'} key={`salary-cert-group8-${uploadId}`}>
                    <MyLittleForm key={`salary-cert-${lastYear3}bonus-${uploadId}`}
                                  item={managerGet(`${lastYear3}bonus`)}/>
                    <div/>
                </div>

            </>
        ],
        getSectionErrors: (rows, mortgage, minimums, section) => {
            if (!rows.length) {
                return []
            }
            let sorted = rows.sort(sorterByDateName('documentDate'))

            let errors = []
            let first = sorted[0]
            let last = sorted[sorted.length - 1]
            const createErrorForMismatchData = (name, applicationData, uploadData, tolerance) => {

                const check = (orig, upload, str) => {

                    if (typeof orig === 'number' && !isNaN(orig) && typeof upload === 'number' && !isNaN(upload)) {
                        let difference = toleranceDifference(orig, upload, tolerance)
                        if (difference) {
                            let way = difference < 0 ? "less" : "more"
                            errors.push(`${str} is ${difference.toLocaleString('en-us')} ${way} than claimed`)
                        }
                    }
                }

                if (typeof applicationData === 'object' && applicationData != null ) {
                    Object.keys(applicationData).forEach(key => {
                        check(applicationData[key], uploadData[key], `${name}: ${key}`)
                    })
                } else {
                    check(applicationData, uploadData, name)
                }

            }

            if (dayjs.isDayjs(last.documentDate)) {
                const uploadData = last.upload.verified.values
                let legacy = legacyIncome(mortgage)
                let applicationData = []
                let job = null
                if (legacy) {
                    job = mortgage[`applicant${section.applicant}`].income
                    if (job?.overtime) {
                        applicationData.push({
                            name: "overtime",
                            amount: job.overtime,
                            guaranteed: false
                        })
                    }
                    if (job.bonus) {
                        applicationData.push({
                            name: "bonus",
                            amount: job.bonus,
                            guaranteed: false
                        })
                    }
                    if (job.commission) {
                        applicationData.push({
                            name: "commission",
                            amount: job.commission,
                            guaranteed: false
                        })
                    }

                } else {
                    job = mortgage[`applicant${section.applicant}`].employmentIncome.find(it => it.id === section.index)
                    applicationData = job.incomes || []
                }


                const shiftAllowance = applicationData.find(it => it.name === "shiftAllowance")
                const carAllowance = applicationData.find(it => it.name === "carAllowance")
                const otherAllowance = applicationData.find(it => it.name === "otherAllowance")
                const overtime = applicationData.find(it => it.name === "overtime")
                const commission = applicationData.find(it => it.name === "commission")
                const bonus = applicationData.find(it => it.name === "bonus")
                createErrorForMismatchData("Gross Basic", job.grossBasic, uploadData.grossBasic)
                createErrorForMismatchData("Car Allowance", {
                    "Last Year": carAllowance?.yearMostRecent || carAllowance?.amount,
                    "Year Before": carAllowance?.yearBefore,
                    "Year Before That": carAllowance?.yearBeforeThat,
                }, {
                    "Last Year": uploadData?.carAllowanceYearMostRecent,
                    "Year Before": uploadData?.carAllowanceYearBefore,
                    "Year Before That": uploadData?.carAllowanceYearBeforeThat,
                })
                createErrorForMismatchData("Shift Allowance", {
                    "Last Year": shiftAllowance?.yearMostRecent || shiftAllowance?.amount,
                    "Year Before": shiftAllowance?.yearBefore,
                    "Year Before That": shiftAllowance?.yearBeforeThat,
                }, {
                    "Last Year": uploadData?.shiftAllowanceYearMostRecent,
                    "Year Before": uploadData?.shiftAllowanceYearBefore,
                    "Year Before That": uploadData?.shiftAllowanceYearBeforeThat,
                })
                createErrorForMismatchData("Other Allowance", {
                    "Last Year": otherAllowance?.yearMostRecent || otherAllowance?.amount,
                    "Year Before": otherAllowance?.yearBefore,
                    "Year Before That": otherAllowance?.yearBeforeThat,
                }, {
                    "Last Year": uploadData?.otherAllowanceYearMostRecent,
                    "Year Before": uploadData?.otherAllowanceYearBefore,
                    "Year Before That": uploadData?.otherAllowanceYearBeforeThat,
                })
                createErrorForMismatchData("Overtime", {
                    "Last Year": overtime?.yearMostRecent || overtime?.amount,
                    "Year Before": overtime?.yearBefore,
                    "Year Before That": overtime?.yearBeforeThat,
                }, {
                    "Last Year": uploadData?.overtimeYearMostRecent,
                    "Year Before": uploadData?.overtimeYearBefore,
                    "Year Before That": uploadData?.overtimeYearBeforeThat,
                })
                createErrorForMismatchData("Commission", {
                    "Last Year": commission?.yearMostRecent || commission?.amount,
                    "Year Before": commission?.yearBefore,
                    "Year Before That": commission?.yearBeforeThat,
                }, {
                    "Last Year": uploadData?.commissionYearMostRecent,
                    "Year Before": uploadData?.commissionYearBefore,
                    "Year Before That": uploadData?.commissionYearBeforeThat,
                })
                createErrorForMismatchData("Bonus", {
                    "Last Year": bonus?.yearMostRecent || bonus?.amount,
                    "Year Before": bonus?.yearBefore,
                    "Year Before That": bonus?.yearBeforeThat,
                }, {
                    "Last Year": uploadData?.bonusYearMostRecent,
                    "Year Before": uploadData?.bonusYearBefore,
                    "Year Before That": uploadData?.bonusYearBeforeThat,
                })
                if (commission?.guaranteed && (commission?.yearMostRecent || commission?.amount) && !uploadData?.commissionGuaranteed) {
                    errors.push(`Commission is not guaranteed according to Salary Cert`)
                }
                if (carAllowance?.guaranteed && (carAllowance?.yearMostRecent || carAllowance.amount) && !uploadData?.carAllowanceGuaranteed) {
                    errors.push(`Car Allowance is not guaranteed according to Salary Cert`)
                }
                if (shiftAllowance?.guaranteed && (shiftAllowance?.yearMostRecent || shiftAllowance.amount) && !uploadData?.shiftAllowanceGuaranteed) {
                    errors.push(`Shift Allowance is not guaranteed according to Salary Cert`)
                }
                if (otherAllowance?.guaranteed && (otherAllowance?.yearMostRecent || otherAllowance.amount) && !uploadData?.otherAllowanceGuaranteed) {
                    errors.push(`Other Allowance is not guaranteed according to Salary Cert`)
                }
                if (overtime?.guaranteed && (overtime?.yearMostRecent || overtime.amount) && !uploadData?.overtimeGuaranteed) {
                    errors.push(`Overtime is not guaranteed according to Salary Cert`)
                }
                if (bonus?.guaranteed && (bonus?.yearMostRecent || bonus.amount) && !uploadData?.bonusGuaranteed) {
                    errors.push(`Bonus is not guaranteed according to Salary Cert`)
                }


                let betterEndDate = last.documentDate
                let {
                    maxAge,
                    requiredRange
                } = minimums
                let [m, ageUnit] = maxAge
                // test the age of the most recent document
                let age = dayjs().diff(betterEndDate, ageUnit, true)

                if (age > m) {
                    errors.push(`Most recent Salary Cert is older than ${m} ${ageUnit} (already in app)`)
                }
                // then check the range of the documents for
                // a) the last doc is not too old - use doc date
                // b) the last doc is too old - use today
            }


            return errors
        },
        SectionApplicationForm: ({
                                     section,
                                     mortgage
                                 }) => {
            let target = section.applicant === 1 ? `applicant1` : `applicant2`
            let questions = [
                {
                    name: 'positionHeld',
                    label: 'Position Held',
                    answer: {
                        type: 'text',
                    },
                    target: !section.index ? `${target}.employment.positionHeld` : `${target}.employmentIncome[${section.index}].positionHeld`
                }, {
                    name: 'publicSector',
                    label: 'Sector',
                    answer: {
                        type: 'select',
                        choices: [
                            {
                                value: true,
                                text: 'Public'
                            }, {
                                value: false,
                                text: 'Private'
                            }
                        ]
                    },
                    target: !section.index ? `${target}.employment.publicSector` : `${target}.employmentIncome[${section.index}].publicSector`
                },
            ]
            return <MortgageForm questions={questions}/>
        }

    }
    const giftCertificateConfig = {
        template: {
            items: [
                {
                    name: 'amount',
                    label: 'Amount',
                    type: 'currency',
                }, {
                    name: 'donor',
                    label: 'Donor',
                    type: 'text',
                }, {
                    name: 'documentDate',
                    label: 'Document Date',
                    type: 'date',
                }
            ]
        },
        sortDate: 'documentDate',
        uploadsSummaryColumns: [
            {
                title: 'Date',
                key: 'documentDate',
                dataIndex: 'documentDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Amount',
                key: 'amount',
                dataIndex: 'amount'
            }, {
                title: 'Donor',
                key: 'donor',
                dataIndex: 'donor'
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let rows = uploads.map(it => ({
                key: it.id,
                amount: valueOrNoData(it, 'amount'),
                donor: valueOrNoData(it, 'donor'),
                documentDate: valueOrNoData(it, 'documentDate'),
                upload: it
            }))
            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getRowLabel: (row) => !!row && !!row.documentDate && row.documentDate.format(LONGDATE),
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <div className="d-row j-equal">
                <MyLittleForm key={`gift-documentDate-${uploadId}`} item={managerGet('documentDate')}/>,
                <MyLittleForm key={`gift-amount-${uploadId}`} item={managerGet('amount')}/>,
            </div>, <MyLittleForm key={`gift-donor-${uploadId}`} item={managerGet('donor')}/>
        ],
    }
    const identificationConfig = {
        template: {
            items: [
                {
                    name: 'documentDate',
                    label: 'Expiry Date',
                    type: 'date',
                }, {
                    name: 'dob',
                    label: 'Date of Birth',
                    type: 'date',
                }, {
                    name: 'fullName',
                    label: 'Full Name',
                    type: 'text',
                }
            ]
        },
        sortDate: 'documentDate',
        uploadsSummaryColumns: [
            {
                title: 'Expiry Date',
                key: 'documentDate',
                dataIndex: 'documentDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'DOB',
                key: 'dob',
                dataIndex: 'dob',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Name',
                key: 'fullName',
                dataIndex: 'fullName',
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let rows = uploads.map(it => {
                // set up the rows
                let item = {
                    key: it.id,
                    documentDate: valueOrNoData(it, 'documentDate'),
                    dob: valueOrNoData(it, 'dob'),
                    fullName: valueOrNoData(it, 'fullName'),
                    upload: it
                }

                // add errors to row
                let errors = []
                if (!dayjs.isDayjs(item.documentDate)) {
                    errors.push(`Expiry Date missing`)
                }
                if (!dayjs.isDayjs(item.dob)) {
                    errors.push(`DOB missing`)
                }
                if (typeof item.fullName !== 'string') {
                    errors.push(`Full Name missing`)
                }
                return {
                    ...item,
                    errors
                }
            })
            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getRowLabel: (row) => !!row && !!row.documentDate && dayjs.isDayjs(row.documentDate) && row.documentDate.format(LONGDATE),
        getSectionErrors: (rows, mortgage, minimums, section) => {
            let errors = []
            let additionalQuestions = []
            if (rows.length) {
                let sorted = rows.sort(sorterByDateName('documentDate'))

                let noneStamped = !sorted.some(it => !!it.upload.stampedOn)
                if (noneStamped) {
                    errors.push(`Document not stamped`)
                }
                let last = sorted[sorted.length - 1]
                let applicant = section.applicant === 1 ? mortgage.applicant1 : mortgage.applicant2

                // check if the document is not going to expiry in the next six months
                if (dayjs.isDayjs(last.documentDate)) {
                    let testExpiryDate = dayjs().add(6, 'months')
                    if (last.documentDate.isBefore(testExpiryDate)) {
                        errors.push(`Document is going to expire in the next 6 months`)
                    }
                }

                // check does the application DOB match the document DOB
                if (dayjs.isDayjs(last.dob)) {
                    if (last.dob.format("YYYY-MM-DD") !== applicant.personal.dateOfBirth) {
                        additionalQuestions.push({
                            name: 'dateOfBirth',
                            label: 'Date of Birth',
                            answer: {
                                type: 'date',
                            },
                            target: section.applicant === 1 ? `applicant1.personal.dateOfBirth` : `applicant2.personal.dateOfBirth`
                        })
                        errors.push(`DOB does not match`)
                    }
                }

                // check names match
                let type = typeof last.fullName
                if (type === 'string') {
                    if (applicant.fullName !== last.fullName) {
                        errors.push(`Full Name does not match`)
                        additionalQuestions.push([
                            {
                                name: 'firstName',
                                text: `Applicant ${section.applicant} First Name`,
                                label: 'FirstName',
                                answer: {
                                    type: 'text',
                                },
                                target: section.applicant === 1 ? `applicant1.firstName` : `applicant2.firstName`
                            }, {
                                name: 'surname',
                                text: `Applicant ${section.applicant} Surname`,
                                label: 'Surname',
                                answer: {
                                    type: 'text',
                                },
                                target: section.applicant === 1 ? `applicant1.surname` : `applicant2.surname`
                            }
                        ])
                    }
                }

            }
            if (additionalQuestions.length) {
                return {
                    errors,
                    additionalQuestions
                }
            }
            return errors
        },
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <MyLittleForm key={`identification-documentDate-${uploadId}`} item={managerGet('documentDate')}/>,
            <MyLittleForm key={`identification-fullName-${uploadId}`} item={managerGet('fullName')}/>,
            <MyLittleForm key={`identification-dob-${uploadId}`} item={managerGet('dob')}/>
        ],
        SectionApplicationForm: ({
                                     section,
                                     additionalQuestions
                                 }) => {
            if (!additionalQuestions || !additionalQuestions.length) {
                return null
            }
            return <MortgageForm questions={additionalQuestions}/>
        },
    }
    const proofOfDepositAvailableConfig = {
        template: {
            items: [
                {
                    name: 'description',
                    label: 'Description',
                    type: 'text',
                },
                {
                    name: 'endDate',
                    label: 'End Date',
                    type: 'date',
                }, {
                    name: 'closingBalance',
                    label: 'Closing Balance',
                    type: 'currency',
                }
            ]
        },
        sortDate: 'endDate',
        uploadsSummaryColumns: [
            {
                title: 'Date',
                key: 'endDate',
                dataIndex: 'endDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            },
            {
                title: 'Description',
                key: 'description',
                dataIndex: 'description',

            }, {
                title: 'Closing Balance',
                key: 'closingBalance',
                dataIndex: 'closingBalance'
            }

        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let rows = uploads.map(it => ({
                key: it.id,
                endDate: valueOrNoData(it, 'endDate'),
                closingBalance: valueOrNoData(it, 'closingBalance'),
                description: valueOrNoData(it, 'description'),
                upload: it
            }))
            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.endDate) && item.endDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getRowLabel: (row) => !!row && !!row.endDate && row.endDate.format(LONGDATE),
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <MyLittleForm key={`pod-available-description-${uploadId}`} item={managerGet('description')}/>,
            <MyLittleForm key={`pod-available-endDate-${uploadId}`} item={managerGet('endDate')}/>,
            <MyLittleForm key={`pod-available-closingBalance-${uploadId}`} item={managerGet('closingBalance')}/>
        ],
    }
    const residencePermitConfig = {
        template: {
            items: [
                // {
                //     name : 'documentDate',
                //     label: 'Document Date',
                //     type : 'date',
                // },
                {
                    name: 'expiryDate',
                    label: 'Expiry Date',
                    type: 'date',
                }
            ]
        },
        sortDate: 'expiryDate',
        uploadsSummaryColumns: [
            {
                title: 'Expiry Date',
                key: 'expiryDate',
                dataIndex: 'expiryDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Errors',
                key: 'errors',
                dataIndex: 'errors',
                render: (text) => <RenderErrors errors={text}/>
            }
            // {
            //     title    : 'Document Date',
            //     key      : 'documentDate',
            //     dataIndex: 'documentDate',
            //     render   : (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            // }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {

            let rows = uploads.map(it => {
                let errors = []
                let obj = {
                    key: it.id,
                    expiryDate: valueOrNoData(it, 'expiryDate'), // documentDate: valueOrNoData(it, 'documentDate'),
                    upload: it
                }
                if (!dayjs.isDayjs(obj.expiryDate)) {
                    errors.push(`Expiry Date missing`)
                }
                return {
                    ...obj,
                    errors
                }
            })
            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.expiryDate) && item.expiryDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getSectionErrors: (rows, mortgage, minimums, section) => {
            if (!rows.length) {
                return []
            }
            let sorted = rows.sort(sorterByDateName('expiryDate'))
            let errors = []
            let last = sorted[sorted.length - 1]
            if (dayjs.isDayjs(last.expiryDate)) {
                let betterEndDate = last.expiryDate.add(1, 'day')
                let {maxAge} = minimums
                let [m, ageUnit] = maxAge
                // test the age of the most recent document
                let age = dayjs().diff(betterEndDate, ageUnit, true)
                if (age > m) {
                    errors.push(`Permit will expire in less than 2 months - NOT IN APP - plz confirm if 2 months is fine`)
                }
            }
            return errors
        },
        getRowLabel: (row) => !!row && !!row.expiryDate && row.expiryDate.format(LONGDATE),
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            // <MyLittleForm key={`residencePermit-documentDate-${uploadId}`} item={managerGet('documentDate')}/>,
            <MyLittleForm key={`residencePermit-expiryDate-${uploadId}`} item={managerGet('expiryDate')}/>
        ],
    }
    const depositReceiptConfig = {
        template: {
            items: [
                {
                    name: 'documentDate',
                    label: 'Document Date',
                    type: 'date',
                }, {
                    name: 'amount',
                    label: 'Amount',
                    type: 'currency',
                }
            ]
        },
        sortDate: 'documentDate',
        uploadsSummaryColumns: [
            {
                title: 'Date',
                key: 'documentDate',
                dataIndex: 'documentDate',
                render: (text) => dayjs.isDayjs(text) ? text.format(LONGDATE) : text
            }, {
                title: 'Amount',
                key: 'amount',
                dataIndex: 'amount'
            }
        ],
        getUploadSummaryRows: (uploads, {
            mortgage,
            section,
            me
        }) => {
            let rows = uploads.map(it => ({
                key: it.id,
                documentDate: valueOrNoData(it, 'documentDate'),
                amount: valueOrNoData(it, 'amount'),
                upload: it
            }))
            let requiredFirst = getFirstRequiredDate(section, rows)
            return rows.map((item, i) => {
                let tooOld = requiredFirst ? dayjs.isDayjs(item.documentDate) && item.documentDate.isBefore(requiredFirst) : false
                return {
                    ...item,
                    tooOld
                }
            })
        },
        getSectionErrors: (rows, mortgage, minimums, section) => {

            if (!rows.length) {
                return []
            }
            let sorted = rows.sort(sorterByDateName('documentDate'))
            let errors = []
            let last = sorted[sorted.length - 1]
            let applicationAmount = mortgage.properties[0]?.bookingDepositPaid
            if (applicationAmount !== last.amount) {
                errors.push(`Amount paid does not match application`)
            }
            return errors
        },
        getRowLabel: (row) => !!row && !!row.documentDate && row.documentDate.format(LONGDATE),
        UploadDataForm: ({
                             uploadId,
                             managerGet
                         }) => [
            <MyLittleForm key={`depositReceipt-documentDate-${uploadId}`} item={managerGet('documentDate')}/>,
            <MyLittleForm key={`depositReceipt-amount-${uploadId}`} item={managerGet('amount')}/>
        ],
        SectionApplicationForm: ({
                                     section,
                                     mortgage
                                 }) => {
            let propertyId = mortgage.properties[0]?.id
            let questions = [
                {
                    label: "Booking Deposit",
                    text: "How much was the deposit you paid?",
                    name: "bookingDepositPaid",
                    answer: {
                        type: "euro",
                    },
                    model: "property",
                    target: `properties[${propertyId}]?.bookingDepositPaid`
                }
            ]
            return <MortgageForm questions={questions}/>
        },
    }
    return {
        investmentStatementConfig,
        cardStatementConfig,
        loanStatementConfig,
        mortgageStatementConfig,
        proofOfAddressConfig,
        accountStatementConfig,
        payslipConfig,
        edsConfig,
        salaryCertConfig,
        giftCertificateConfig,
        identificationConfig,
        proofOfDepositAvailableConfig,
        residencePermitConfig,
        depositReceiptConfig
    }
}

function ScannedRows({
                         upload,
                         mortgage,
                         sectionScan,
                         fieldName
                     }) {
    const [analysis, setAnalysis] = useState(null)
    const [raw, setRaw] = useState(null)
    const handleTransactionsClick2 = async () => {
        const getUserMortgageUploads = /* GraphQL */ `
            query GetUserMortgageUploads($id: ID!) {
                getUserMortgageUploads(id: $id) {
                    id
                    processedAnalysis
                    analysis
                }
            }
        `;
        const result = await performGQL({id: upload.id}, getUserMortgageUploads)

        const analysis = JSON.parse(result.processedAnalysis)


        const dayjs = require('dayjs')
        const customParseFormat = require("dayjs/plugin/customParseFormat");
        dayjs.extend(customParseFormat);

        // function bankStatement(uploadId, analysis) {
        // 	const errors = []
        // 	function textractToUploadData({
        // 		queries,
        // 		tables: rawTables,
        // 		forms,
        // 		noOfPages
        // 	})
        // 	{
        // 		console.log(JSON.parse(JSON.stringify({
        // 			queries,
        // 			rawTables,
        // 			forms,
        // 			noOfPages
        // 		})))
        // 		const tables = Object.freeze(rawTables)
        //
        // 		const firstDateInRows = (rows) => {
        // 			let date = null
        // 			for (let i = 0; i < rows.length; i++) {
        // 				let cell = Array.isArray(rows[i]) ? rows[i][0] : rows[i].cells[0]
        // 				if (dayjs.isDayjs(cell)) {
        // 					date = cell
        // 					break
        // 				}
        // 				date = parseDateString(cell)
        // 				if (Array.isArray(date)) {
        // 					// multiple dates in the first column - return the first one
        // 					date = date[0]
        // 				}
        // 				if (date) {
        // 					break
        // 				}
        // 			}
        // 			return date
        // 		}
        // 		const lastDateInRows = (rows) => {
        //
        // 			let date = null
        // 			for (let i = rows.length - 1; i >= 0; i--) {
        // 				let cell = Array.isArray(rows[i]) ? rows[i][0] : rows[i].cells[0]
        // 				if (dayjs.isDayjs(cell)) {
        // 					date = cell
        // 					break
        // 				}
        // 				date = parseDateString(cell)
        // 				if (Array.isArray(date)) {
        // 					// multiple dates in the first column - return the last one
        // 					date = date[date.length - 1]
        // 				}
        // 				if (date) {
        // 					break
        // 				}
        // 			}
        // 			return date
        // 		}
        // 		const parseDateString = (dateString) => {
        // 			if (!dateString) {
        // 				return null
        // 			}
        // 			if (dayjs.isDayjs(dateString) && dateString.isValid()) {
        // 				return dateString
        // 			}
        // 			const monthMap = {
        // 				JAN: "Jan",
        // 				FEB: "Feb",
        // 				MAR: "Mar",
        // 				APR: "Apr",
        // 				MAY: "May",
        // 				JUN: "Jun",
        // 				JUL: "Jul",
        // 				AUG: "Aug",
        // 				SEP: "Sep",
        // 				OCT: "Oct",
        // 				NOV: "Nov",
        // 				DEC: "Dec"
        // 			};
        //
        // 			const normalizeDate = (dateStr) => {
        // 				return dateStr.replace(/\s+/g, "").replace(/[A-Z]{3}/g, (m) => monthMap[m.toUpperCase()] || m);
        // 			};
        //
        // 			const possibleFormats = [
        // 				"D MMM YYYY",
        // 				"D MMMM YYYY",
        // 				"DD/MM/YYYY",
        // 				"D/MM/YYYY",
        // 				"DMMMYYYY",
        // 				"D-MM-YYYY",
        // 				"YYYY-MM-D",
        // 				"D MMM YY",
        // 				"D MMMM YY",
        // 				"DMMMYY",
        // 				"D/MM/YY",
        // 				"D-MM-YY",
        // 				"DD MMM YYYY",
        // 				"DD MMMM YYYY",
        // 				"DDMMMYYYY",
        // 				"DD-MM-YYYY",
        // 				"YYYY-MM-DD",
        // 				"DD MMM YY",
        // 				"DD MMMM YY",
        // 				"DDMMMYY",
        // 				"MMM DD, YYYY",
        // 				"MMM D, YYYY",
        // 				"MMM DD, YY",
        // 				"MMM D, YY",
        // 				"DD/MM/YY",
        // 				"DD-MM-YY",
        // 			];
        // 			function getFormatRegex(format) {
        // 				// Define mappings from format tokens to regex patterns
        // 				const formatToRegex = {
        // 					"D": "\\d{1,2}",
        // 					"DD": "\\d{2}",
        // 					"MMM": "[A-Za-z]{3}",
        // 					"MMMM": "[A-Za-z]+",
        // 					"YYYY": "\\d{4}",
        // 					"YY": "\\d{2}",
        // 				};
        //
        // 				// Escape separators in the format and replace tokens with regex
        // 				let regexPattern = format.replace(/D{1,2}|M{3,4}|Y{2,4}/g, match => formatToRegex[match] || match);
        //
        // 				return new RegExp(regexPattern, "g"); // Global flag to find all occurrences
        // 			}
        // 			function parseUnknownDate(dateString) {
        // 				for (const format of possibleFormats) {
        // 					let cleanedString = normalizeDate(dateString)
        // 					let parsedDate = dayjs(cleanedString, format, true);
        // 					if (parsedDate.isValid()) {
        // 						return parsedDate;
        // 					}
        // 					// check that mutiple dates have not been confused together
        // 					//const isMultipleDates = dateString.trim().split(/\s|\/|-|(?<=\d)(?=[A-Za-z]{3})|(?<=[A-Za-z]{3})(?=\d)/).length > 3;
        // 					// Return array of parsed dated if multiple dates are detected
        // 					let regex = getFormatRegex(format)
        // 					let matches = cleanedString.match(regex)
        // 					if (matches && matches.length > 1) {
        // 						let parsedDates = []
        // 						for (const match of matches) {
        // 							let partDate = dayjs(normalizeDate(match), format, true)
        // 							if (partDate.isValid()) {
        // 								parsedDates.push(partDate)
        // 							}
        // 						}
        // 						if (parsedDates.length > 1) {
        // 							return parsedDates
        // 						}
        // 					}
        //
        //
        //
        // 				}
        // 				return null; // Return null if no format matches
        // 			}
        // 			try {
        // 				return parseUnknownDate(dateString)
        // 			}
        // 			catch (e) {
        // 				console.error('Error parsing date', e, dateString)
        // 			}
        //
        // 		};
        // 		const getTableDirection = (rows) => {
        // 			let firstDate = firstDateInRows(rows)
        // 			let lastDate = lastDateInRows(rows)
        // 			return firstDate && lastDate && firstDate.isBefore(lastDate) ? 'asc' : 'desc'
        // 		}
        // 		const isNumeric = (str) => {
        // 			if (!str) {
        // 				return false
        // 			}
        // 			let n = str.replace(/\D/g, '')
        // 			if (n === "") {
        // 				return false
        // 			}
        // 			return !isNaN(n)
        // 		}
        // 		const numericValue = (str) => {
        // 			if (!str) {
        // 				return 0
        // 			}
        // 			let cleaned = str.trim().replace(/[^0-9.,-]/g, "");
        //
        // 			let dotIndex = cleaned.indexOf(".");
        // 			let commaIndex = cleaned.indexOf(",");
        //
        // 			let dotCount = (cleaned.match(/\./g) || []).length;
        // 			let commaCount = (cleaned.match(/,/g) || []).length;
        //
        // 			// Case 1: If only one separator exists, assume it's the decimal separator
        // 			if (dotCount === 0 && commaCount === 1) {
        // 				cleaned = cleaned.replace(",", "."); // Convert to standard decimal
        // 			} else if (dotCount === 1 && commaCount === 0) {
        // 				// If only one dot exists, it's already the decimal separator (US format)
        // 			} else if (dotCount > 1 && commaCount === 0) {
        // 				// Case 2: Multiple dots → assume dot is thousand separator, remove it
        // 				cleaned = cleaned.replace(/\./g, "");
        // 			} else if (commaCount > 1 && dotCount === 0) {
        // 				// Case 3: Multiple commas → assume comma is thousand separator, remove it
        // 				cleaned = cleaned.replace(/,/g, "")
        // 			}
        // 			else if (commaCount > 1 && dotCount === 1) {
        // 				// ✅ FIXED: Case 3: Multiple commas & one dot (US format)
        // 				cleaned = cleaned.replace(/,/g, ""); // Remove commas (thousands separators)
        // 			}
        // 			else if (dotCount === 1 && commaCount === 1) {
        // 				// Case 4: Mixed separators
        // 				if (dotIndex > commaIndex) {
        // 					// If dot appears first, assume US format (1,234.56)
        // 					cleaned = cleaned.replace(/,/g, ""); // Remove thousands separator
        // 				} else {
        // 					// If comma appears first, assume European format (1.234,56)
        // 					cleaned = cleaned.replace(/\./g, "").replace(",", ".");
        // 				}
        // 			} else if (dotCount > 1 && commaCount > 1) {
        // 				// Case 5: Multiple occurrences of both (invalid, fallback)
        // 				return NaN;
        // 			}
        // 			return parseFloat(cleaned)
        // 		}
        // 		const hasAmounts = (cells) => {
        // 			try{
        // 				return isNumeric(cells[2]) || isNumeric(cells[3]) || (cells.length > 4 && isNumeric(cells[4]))
        // 			}
        // 			catch (e){
        // 				console.error('Error checking for amounts', e, cells)
        // 				throw e
        // 			}
        // 		}
        // 		const extractAccountTransactions = () => {
        //
        // 			const isTransactionTable = (rows) => {
        //
        // 				// none of the cells in the first row contains numbers , ie header titles
        // 				let max = rows.length
        // 				// nearly every row should have a description &&
        // 				// if nearly all entries in the first column are dates, then this is a transaction table
        // 				let rowsWithDescription = 0
        // 				let entriesInFirstColumn = []
        // 				for (let i = 0; i < max; i++) {
        // 					const cell1 = rows[i][0]
        // 					const cell2 = rows[i][1]
        // 					if (!!cell2) {
        // 						rowsWithDescription++
        // 					}
        //
        // 					if (cell1 && cell1.trim() !== '') {
        // 						entriesInFirstColumn.push(cell1)
        // 					}
        // 				}

        // 				let column1WithDates = entriesInFirstColumn.filter(cell => parseDateString(cell))
        // 				// if more than 90% of the entries in the first column are dates, then this is a transaction table
        // 				let col1IsMostlyDates = column1WithDates.length / entriesInFirstColumn.length > 0.5
        // 				// if more that 90% of the rows have a description, then this is a transaction table
        // 				let col2MostlyDescriptions = (rowsWithDescription / max) > 0.7

        // 					max,
        // 					percent : rowsWithDescription / max,
        // 					rowsWithDescription,
        // 					col2MostlyDescriptions,
        // 					col1IsMostlyDates,
        // 					column1WithDates
        // 				})
        // 				return col2MostlyDescriptions && col1IsMostlyDates
        // 			}
        // 			const getVerticallyMergedRows = (rows) => {
        // 				let betterRows = []
        // 				let currentDate = null
        // 				let extraneousDates = []
        // 				rows.forEach((cells, i) => {
        // 					let date = parseDateString(cells[0])
        // 					let description = cells[1]
        // 					let hasFigures = hasAmounts(cells)
        //
        // 					// always add a dated row to the table
        // 					if (date) {
        // 						if (Array.isArray(date)) {
        // 							currentDate = date.shift()
        // 							extraneousDates = date
        // 						}
        // 						else{
        // 							currentDate = date
        // 						}
        // 						betterRows.push(cells.map((x,i)=>i===0?currentDate:x))
        // 					}
        // 					// if it's a header or any non-transaction, just add it
        // 					else if (i < 3 && cells.every(cell => !isNumeric(cell))) {
        // 						// add header
        // 						betterRows.push(cells)
        // 					}
        // 					// if no date and also no figures, merge description into previous row
        // 					// it is possible that previous row mistakenly has the date
        // 					else if (!hasFigures) {
        // 						// merge into previous row
        // 						if (extraneousDates.length) {
        // 							let newDate = extraneousDates.shift()
        // 							betterRows.push(cells.map((x,i)=>i===0?newDate:x))
        // 						}
        // 						else if (description.trim() !== '') {
        // 							betterRows[betterRows.length - 1][1] += '\n' + description
        // 						}
        // 					}
        // 					// if the previous row has no figures, merge this row figures and destription into it
        // 					else if (betterRows[betterRows.length - 1] && !hasAmounts(betterRows[betterRows.length - 1])) {
        // 						// merge into previous row
        // 						betterRows[betterRows.length - 1][1] += '\n' + description
        // 						betterRows[betterRows.length - 1][2] = cells[2]
        // 						betterRows[betterRows.length - 1][3] = cells[3]
        // 						if (cells.length > 3) {
        // 							betterRows[betterRows.length - 1][4] = cells[4]
        // 						}
        // 					}
        //
        // 					// otherwise if there's figures, is a new transaction on current date
        // 					else if (hasFigures) {
        // 						betterRows.push(cells.map((x,i)=>i===0?currentDate:x))
        // 					}
        // 					else {
        // 						console.warn('WHat when on here ? ', cells)
        // 					}
        // 				})
        //
        // 				// remove all the bottom rows that are not transactions
        // 				for (let i = betterRows.length - 1; i >= 0; i--) {
        // 					if (!isNumeric(betterRows[i][2]) && !isNumeric(betterRows[i][3])) {
        // 						betterRows.pop()
        // 					}
        // 					else {
        // 						break
        // 					}
        // 				}
        // 				return betterRows
        // 			}
        // 			const cleanUpEmptyColumns = (rows) => {
        // 				const nCols = Math.max(...rows.map(row => row.cells.length))
        // 				if (nCols && nCols > 5) {
        // 					let index = nCols - 1
        // 					// if all cells in the last column are not numeric, remove the column
        // 					while (index >= 5) {
        // 						let cellContents = rows.map(row => row.cells[index])
        // 						let notNumericCells = cellContents.filter(cell => !isNumeric(cell))
        // 						let mostlyTextCells = (notNumericCells.length / cellContents.length) > 0.8
        // 						if (mostlyTextCells) {
        // 							console.log('Removing Empty column' + (index + 1))
        // 							rows.forEach(row => {
        // 								row.cells.splice(index, 1)
        // 							})
        // 						}
        // 						index--
        // 					}
        // 				}
        // 				return rows
        // 			}
        // 			const isSubAccountDetected = (previousRows, nextRows, direction) => {
        // 				// the new rows balance can't be same as previous rows balance
        // 				let result
        // 				console.log(JSON.parse(JSON.stringify({nextRows})))
        // 				let firstDateOfNextTable = firstDateInRows(nextRows)
        // 				let lastDateOfPreviousTable = lastDateInRows(previousRows)
        // 				if (direction === 'desc') {
        // 					result = firstDateOfNextTable && lastDateOfPreviousTable && firstDateOfNextTable.isAfter(lastDateOfPreviousTable)
        // 				}
        // 				else {
        // 					result = firstDateOfNextTable && lastDateOfPreviousTable && firstDateOfNextTable.isBefore(lastDateOfPreviousTable)
        // 				}
        // 				if (!!result) {
        // 					console.log('SUB ACCOUNT DETECTED!!')

        // 						result,
        // 						firstDateOfNextTable   : firstDateOfNextTable.format('D MMM YYYY'),
        // 						lastDateOfPreviousTable: lastDateOfPreviousTable.format('D MMM YYYY')
        // 					})
        // 				}
        // 				return result
        // 			}
        // 			let nonTransactionsPages = Array.from({length: noOfPages}, (_, i) => i + 1) // set all to non transactions
        // 			let subAccounts = []
        // 			const transactionsTables = tables.filter(table => {
        // 				const isTransactions = isTransactionTable(table.rows)
        // 				if (isTransactions) {
        // 					// remove this pageNo from nonTransactionPages if it's still there
        // 					nonTransactionsPages = nonTransactionsPages.filter(page => page !== table.pageNo)
        // 				}
        // 				return isTransactions
        // 			})
        // 			console.log(JSON.parse(JSON.stringify({tables,transactionsTables})))
        // 			transactionsTables.forEach(table => {
        // 				const {
        // 						  rows,
        // 						  ...rest
        // 					  } = table
        // 				const betterRows = getVerticallyMergedRows(rows)
        // 				console.log(JSON.parse(JSON.stringify({
        // 					betterRows
        // 				})))
        // 				if (!subAccounts.length) {
        // 					// this is the first transaction table
        // 					subAccounts.push({rows: [], ...rest})
        // 				}
        // 				else if (isSubAccountDetected(subAccounts[subAccounts.length - 1].rows, betterRows, getTableDirection(betterRows))) {
        // 					// is this a new sub account table? check if the first date in col1 is same or more recent than the last date in the last sub account table
        // 					// if so, create a new sub account
        // 					console.log('!!!!!!!!!! sub account detected !!!!!!!!!!')
        // 					subAccounts.push({
        // 						rows : [],
        // 						vault: true, ...rest
        // 					})
        // 				}
        //
        // 				const rowsWithKeys = betterRows.map(cells => {
        // 					return {
        // 						cells: cells.map(c => dayjs.isDayjs(c) ? c.format('YYYY-MM-DD') : c),
        // 						key  : JSON.stringify(cells)
        // 					}
        // 				})
        // 				rowsWithKeys.forEach(row => {
        // 					// add this row to the last sub account table
        // 					subAccounts[subAccounts.length - 1].rows.push(row)
        // 				})
        // 			})
        // 			console.log(JSON.parse(JSON.stringify({
        // 				transactionsTables,
        // 				subAccounts
        // 			})))
        // 			subAccounts = subAccounts.map(subAccount => {
        // 				const rows = cleanUpEmptyColumns(subAccount.rows)
        // 				if (getTableDirection(rows) === 'desc') {
        // 					rows.reverse()
        // 				}
        // 				return {
        // 					...subAccount,
        // 					rows
        // 				}
        // 			})
        // 			return {
        // 				subAccounts,
        // 				omit: nonTransactionsPages
        // 			}
        // 		}
        // 		const extractFields = () => {
        // 			const {
        // 					  subAccounts,
        // 					  omit
        // 				  } = extractAccountTransactions()
        // 			const scan = {
        // 				subAccounts,
        // 				omit,
        // 				fields: {
        // 					queries
        // 				},
        // 				noOfPages,
        // 			}
        // 			const extractAccountNo = () => {
        // 				//Account No.
        // 				if (queries.hasOwnProperty('IBAN')) {
        // 					scan.fields.accountNumber = queries.IBAN
        // 				}
        // 				else if (queries.hasOwnProperty('AccountNumber')) {
        // 					scan.fields.accountNumber = queries.AccountNumber
        // 				}
        // 				else {
        // 					errors.push('Account Number not found')
        // 				}
        // 			}
        // 			const extractCurrency = () => {
        // 				//Currency
        // 				if (queries.hasOwnProperty('Currency')) {
        // 					scan.fields.currency = queries.Currency
        // 				}
        // 				else {
        // 					errors.push('Currency not found')
        // 				}
        // 			}
        // 			const extractStartDate = () => {
        // 				//Start Date
        // 				let bestStartDates = []
        // 				if (queries.hasOwnProperty('FirstDate')) {
        // 					let d1 = parseDateString(queries.FirstDate)
        // 					if (d1) {
        // 						bestStartDates.push(d1)
        // 					}
        // 				}
        // 				if (queries.hasOwnProperty('DateRange')) {
        // 					console.log('VERY IMPORTANT DATE RANGE - change code to reflect', queries.DateRange)
        //
        // 				}
        // 				scan.subAccounts.forEach(account => {
        // 					const dir = getTableDirection(account.rows)
        // 					const firstDate = dir === 'desc' ? lastDateInRows(account.rows.map(row => row.cells)) : firstDateInRows(account.rows.map(row => row.cells))
        // 					bestStartDates.push(firstDate)
        // 				})
        // 				if (!bestStartDates.length) {
        // 					errors.push('Start Date not found')
        // 				}
        // 				else {
        // 					const oldestOfAllStartDates = bestStartDates.reduce((oldest, current) => current.isBefore(oldest) ? current : oldest);
        // 					scan.fields.startDate = oldestOfAllStartDates.format('YYYY-MM-DD')
        // 				}
        //
        // 			}
        // 			const extractEndDate = () => {
        // 				//End Date
        // 				let bestEndDates = []
        // 				if (queries.hasOwnProperty('LastDate')) {
        // 					let d2 = parseDateString(queries.LastDate)
        // 					if (d2) {
        // 						bestEndDates.push(d2)
        // 					}
        // 				}
        // 				if (queries.hasOwnProperty('DateRange')) {
        // 					console.log('VERY IMPORTANT DATE RANGE - change code to reflect', queries.DateRange)
        //
        // 				}
        // 				if (queries.hasOwnProperty('PrintDate')) {
        // 					let d3 = parseDateString(queries.PrintDate)
        // 					if (d3) {
        // 						bestEndDates.push(d3)
        // 					}
        // 				}
        // 				scan.subAccounts.forEach(account => {
        // 					const dir = getTableDirection(account.rows)
        // 					const lastDate = dir === 'asc' ? lastDateInRows(account.rows.map(row => row.cells)) : firstDateInRows(account.rows.map(row => row.cells))
        // 					bestEndDates.push(lastDate)
        // 				})
        // 				if (!bestEndDates.length) {
        // 					errors.push('End Date not found')
        // 				}
        // 				else {
        // 					const newestOfAllEndDates = bestEndDates.reduce((newest, current) => current.isAfter(newest) ? current : newest);
        // 					scan.fields.endDate = newestOfAllEndDates.format('YYYY-MM-DD')
        // 				}
        //
        // 			}
        // 			const extractBalances = (ascendingRows) => {
        // 				//OPENING BALANCE
        // 				// does this table start with an opening balance - identified by having a balance amt with no transaction
        // 				// find first numerical balance in the table
        // 				const getTrueBalanceFromNonForwarded = (prevRowCells, nextRowCells) => {
        // 					let prevBalance = numericValue(prevRowCells[4])
        // 					console.log('Opening Balance is the first transaction in the table + or - the transaction amount')
        // 					let newBalance = numericValue(nextRowCells[4])
        // 					// assume credit is in col[2] and debit in col[3]
        // 					const getCreditDebitCols = () => {
        //
        // 						if (isNumeric(nextRowCells[2])) {
        //
        // 							// try credit - adding the number to the previous balance should give us the same new balance
        // 							let amt2 = numericValue(nextRowCells[2])
        // 							if (amt2) {
        // 								if (newBalance.toFixed(2) === (prevBalance + amt2).toFixed(2)) {
        // 									console.log('Credit detected in col[2]')
        // 									return {
        // 										credit: 2,
        // 										debit : 3
        // 									}
        // 								}
        // 								if (newBalance.toFixed(2) === (prevBalance - amt2).toFixed(2)) {
        // 									console.log('Debit detected in col[2]')
        // 									return {
        // 										credit: 3,
        // 										debit : 2
        // 									}
        // 								}
        // 							}
        // 						}
        // 						if (isNumeric(nextRowCells[3])) {
        // 							let amt3 = numericValue(nextRowCells[3])
        // 							if (amt3) {
        // 								if (newBalance.toFixed(2) === (prevBalance + amt3).toFixed(2)) {
        // 									console.log('Credit detected in col[3]')
        // 									return {
        // 										credit: 3,
        // 										debit : 2
        // 									}
        // 								}
        // 								if (newBalance.toFixed(2) === (prevBalance - amt3).toFixed(2)) {
        // 									console.log('Debit detected in col[3]')
        // 									return {
        // 										credit: 2,
        // 										debit : 3
        // 									}
        // 								}
        // 							}
        // 						}
        // 						return null
        // 					}
        // 					const creditDebitCols = getCreditDebitCols()
        //
        // 					if (creditDebitCols) {
        // 						if (isNumeric(prevRowCells[creditDebitCols.credit])) {
        // 							return (numericValue(prevRowCells[4]) - numericValue(prevRowCells[creditDebitCols.credit]))
        // 						}
        // 						if (isNumeric(prevRowCells[creditDebitCols.debit])) {
        // 							return (numericValue(prevRowCells[4]) + numericValue(prevRowCells[creditDebitCols.debit]))
        // 						}
        // 					}
        // 					errors.push('Could not detect credit or debit columns')
        // 					return null
        //
        // 				}
        // 				let openingBalance = 0
        // 				let row1 = null
        //
        // 				for (const [index, row] of ascendingRows.entries()) {
        // 					let cells = row.cells
        // 					if (hasAmounts(cells)) {
        // 						if (cells.length > 4 && isNumeric(cells[4])) {
        // 							if (!isNumeric(cells[2]) && !isNumeric(cells[3])) {
        // 								openingBalance = numericValue(cells[4])
        // 								break
        // 							}
        // 							else if (isNumeric(cells[2]) || isNumeric(cells[3])) {
        // 								if (!row1) {
        // 									row1 = cells
        // 								}
        // 								else {
        // 									openingBalance = getTrueBalanceFromNonForwarded(row1, cells)

        // 									break
        // 								}
        // 							}
        //
        // 						}
        // 					}
        // 				}
        // 				let closingBalance = null
        // 				for (let i = ascendingRows.length - 1; i >= 0; i--) {
        // 					let cells = ascendingRows[i].cells
        // 					if (hasAmounts(cells)) {
        // 						if (cells.length > 4 && isNumeric(cells[4])) {
        // 							closingBalance = numericValue(cells[4])
        // 							break
        // 						}
        // 					}
        // 				}
        // 				return {
        // 					openingBalance,
        // 					closingBalance
        // 				}
        // 				//CLOSING BALANCE
        // 			}
        //
        // 			extractAccountNo()
        // 			extractCurrency()
        // 			extractStartDate()
        // 			extractEndDate()
        // 			let totalOpening = 0
        // 			let totalClosing = 0
        // 			scan.subAccounts.forEach(account => {
        // 				const {
        // 						  openingBalance,
        // 						  closingBalance
        // 					  } = extractBalances(account.rows)

        // 					openingBalance,
        // 					closingBalance
        // 				})
        // 				totalOpening += Number((openingBalance || 0).toFixed(2))
        // 				totalClosing += Number((closingBalance || 0).toFixed(2))
        // 			})
        // 			scan.fields.openingBalance = totalOpening
        // 			scan.fields.closingBalance = totalClosing
        //
        // 			return scan
        // 		}
        // 		return extractFields()
        // 	}
        //
        // 	const statement = textractToUploadData(analysis)
        // 	const uploadData = []
        //
        // 	if (!!statement.fields.accountNumber){
        // 		uploadData.push({
        // 			name : 'iban',
        // 			value: statement.fields.accountNumber,
        // 			type : 'TEXT',
        // 		})
        // 	}
        // 	if (!!statement.fields.openingBalance){
        // 		uploadData.push({
        // 			name : 'openingBalance',
        // 			value: statement.fields.openingBalance,
        // 			type : 'NUMBER',
        // 		})
        // 	}
        // 	if (!!statement.fields.closingBalance){
        // 		uploadData.push({
        // 			name : 'closingBalance',
        // 			value: statement.fields.closingBalance,
        // 			type : 'NUMBER',
        // 		})
        // 	}
        // 	if (!!statement.fields.startDate){
        // 		uploadData.push({
        // 			name : 'startDate',
        // 			value: statement.fields.startDate,
        // 			type : 'DATE',
        // 		})
        // 	}
        // 	if (!!statement.fields.endDate){
        // 		uploadData.push({
        // 			name : 'endDate',
        // 			value: statement.fields.endDate,
        // 			type : 'DATE',
        // 		})
        // 	}
        //
        //
        // 	return {
        // 		processedAnalysis: {
        // 			uploadId,
        // 			statement
        // 		},
        // 		uploadData,
        // 		errors
        // 	}
        // }
        // const parsed = bankStatement(upload.id, JSON.parse(result.analysis))

        setAnalysis(analysis)
    }
    const handleStart = async () => {
        const key = getUserUploadKey(upload)
        const post = {
            mortgageID: mortgage.id,
            upload: {
                id: upload.id,
                key: `private/${upload.identityID}/${key}`
            }
        }

        const result = await API.post('sussdAdminAPI', '/operations/s3/extract-statement', {
            body: post
        })

    }
    const handleHasErrorClick = async () => {
        const update = {
            target: `uploads[${upload.id}]`,
            pairs: {
                analysisErrors: !upload.analysisErrors ? 1 : 0
            }
        }
        await mortgage.mutate({update})
    }
    const handleTransactionReset = async () => {
        const update = {
            target: `uploads[${upload.id}]`,
            pairs: {
                analysisJobStatus: null,
                analysisJobID: null
            }
        }
        await mortgage.mutate({update})
    }
    let tables = null

    if (analysis) {

        let subAccounts = null
        if (analysis.statement) {
            subAccounts = analysis.statement.subAccounts
        } else {
            //const manual = textractToUploadData(analysis)
            //subAccounts = manual.subAccounts
        }

        tables = subAccounts.map((account, i) => {
            const rows = account.rows
            const maxNumberOfColumns = Math.max(...rows.map(row => row.cells.length))
            const columns = Array.from({length: maxNumberOfColumns}, (_, i) => {
                return {
                    title: i,
                    dataIndex: i,
                    key: i
                }
            })
            const dataSource = rows.map((row, i) => {
                return {
                    key: i, ...row.cells.reduce((acc, item, index) => {
                        let cellValue = item
                        if (index === 0) {
                            if (dayjs.isDayjs(cellValue)) {
                                cellValue = cellValue.format("D MMM YYYY")
                            }
                            let testDate = dayjs(cellValue, "YYYY-MM-DD", true)
                            if (testDate?.isValid()) {
                                cellValue = testDate.format("D MMM YYYY")
                            }
                        }
                        return {
                            ...acc,
                            [index]: cellValue
                        }
                    }, {})
                }
            })

            return (<div key={i}>
                <h2>Group # {i}</h2>
                <Table dataSource={dataSource} columns={columns} pagination={false}/>
            </div>)
        })

        // tables = analysis.tables.map((table, i) => {
        //         const rows = table.rows
        //         const maxNumberOfColumns = Math.max(...rows.map(row => row.length))
        //         const columns = Array.from({length: maxNumberOfColumns}, (_, i) => {
        //                 return {
        //                         title    : i,
        //                         dataIndex: i,
        //                         key      : i
        //                 }
        //         })
        //         const dataSource = rows.map((row, i) => {
        //                 return {
        //                         key: i,
        //                         ...row.reduce((acc, item, index) => {
        //                                 return {
        //                                         ...acc,
        //                                         [index]: item
        //                                 }
        //                         }, {})
        //                 }
        //         })
        //         return (
        //         <div key={i}>
        //                 <h2># {i}</h2>
        //                 <Table dataSource={dataSource} columns={columns} pagination={false}/>
        //         </div>
        //         )
        // })
    }

    const DeleteButton = ({as}) => {
        return <Popconfirm title={'Are you sure?'} onConfirm={handleTransactionReset} okText="Yes" cancelText="No">
            {as ? <Button><DeleteOutlined/></Button> : <DeleteOutlined/>}
        </Popconfirm>
    }

    useEffect(() => {

        (async function () {
            if (upload.analysisJobStatus === UploadAnalysisStatus.PROCESSED && upload.verified.answered.none) {
                console.log('fetching analysis')
                const fetchAnalysis = async () => {
                    const getUserMortgageUploads = /* GraphQL */ `
                        query GetUserMortgageUploads($id: ID!) {
                            getUserMortgageUploads(id: $id) {
                                id
                                processedAnalysis
                            }
                        }
                    `;
                    const result = await performGQL({id: upload.id}, getUserMortgageUploads)
                    return (JSON.parse(result.processedAnalysis))
                }
                const analysisTemp = await fetchAnalysis()

                if (analysisTemp) {
                    const fields = analysisTemp[fieldName].fields
                    Object.keys(fields).forEach(key => {
                        if (key.toLowerCase().indexOf('date') > -1 && typeof fields[key] === 'string') {
                            fields[key] = dayjs(fields[key], "YYYY-MM-DD", true)
                        }
                    })
                    console.log('setting date .. ')
                    upload.verified.handleScan(analysisTemp[fieldName].fields)
                    sectionScan(analysisTemp[fieldName].fields)
                }
            }
        })()
    }, [upload.analysisJobStatus]);

    if (!upload.analysisJobStatus) {
        return <ButtonAsync onClick={handleStart}>Start Extraction</ButtonAsync>
    }
    if (upload.analysisJobStatus === 'PROCESSING') {
        return <Tag color="orange">Processing <DeleteButton/></Tag>
    }
    if (upload.analysisJobStatus === 'PENDING') {
        return <Tag color="default">Pending <DeleteButton/></Tag>
    }
    if (upload.analysisJobStatus === 'FAILED') {
        return <Tag color="red">Failed <DeleteButton/></Tag>
    }

    return <>
        <Modal width={1800} open={!!analysis} cancelText={false} onOk={false} onCancel={() => setAnalysis(null)}>
            {tables}
        </Modal>
        <Button.Group>
            <ButtonAsync onClick={handleTransactionsClick2}>Transactions</ButtonAsync>
            <CustomPrimary color={upload.analysisErrors ? red[4] : null}>
                <ButtonAsync className={"px-0"} type={upload.analysisErrors ? "primary" : "default"}
                             onClick={handleHasErrorClick}><BugOutlined/></ButtonAsync>
            </CustomPrimary>

            <DeleteButton as="button"/>
        </Button.Group>


    </>
}

export class VerifiableSection {

    constructor(mortgage, section, me) {
        this.mortgage = mortgage
        this.section = section
        this.config = null
        this.me = me
        this.isAccount = false // reset in setSectionConfig
        this.isVerifiable = true // reset in set setSectionConfig
        this.setSectionConfig()
        this.setUploads()

        if (this.isVerifiable) {
            this.updateMortgageTimer = null
            this.minimums = getMinimumsForSection(this.section)
            this.additionalQuestions = []
            this.setRows()
            this.setErrors()
        }


    }

    uploadDataGetter = (upload) => { // the 'manager' in managerGet
        const createVerifiedRow = async (name, verifiedValue, verifiedValueType) => {
            let result = await performGQL({
                input: {
                    uploadID: upload.id,
                    mortgageID: this.mortgage.id,
                    name,
                    verifiedValue,
                    verifiedValueType,
                    verificationOn: dayjs().toISOString(),
                    verificationBy: this.me.sub,
                    supervisorCheckBy: 'null'
                }
            }, createAdminMortgageUploadVerifiedPoint)
            return result
        }
        const updateVerifiedRow = async (row, pairs) => {
            return await updateUploadVerifiedRow(this.mortgage, row, pairs)

        }
        const createMessage = async (verificationID, message) => {
            let result = await performGQL({
                input: {
                    verificationID,
                    authorID: this.me.sub,
                    string: message
                }
            }, createAdminMortgageSectionVerificationNote)
            this.mortgage.setMortgage(prevMortgage => {
                return {
                    ...prevMortgage,
                    uploads: prevMortgage.uploads.map(item => {
                        if (item.id === upload.id) {
                            return {
                                ...item,
                                verifiedData: {
                                    ...item.verifiedData,
                                    items: item.verifiedData.items.map(it => {
                                        if (it.id === verificationID) {
                                            return {
                                                ...it,
                                                verificationNotes: {
                                                    ...it.verificationNotes,
                                                    items: [
                                                        ...it.verificationNotes.items, result
                                                    ]
                                                }
                                            }
                                        }
                                        return it
                                    })
                                }
                            }
                        }
                        return item
                    })
                }
            })
        }
        let customTemplate = this.config.hasOwnProperty('customTemplate') ? this.config.customTemplate(this) : null
        let customItems = customTemplate ? customTemplate.items : []

        return (name) => {
            let item = [
                ...this.config.template.items, ...customItems
            ].find(item => item.name === name)
            let all = !!upload.verifiedData ? upload.verifiedData.items.filter(item => item.name === name) : []
            let data = all.length ? all[0] : null

            return (mortgage) => {
                const mortgageUpload = data ? mortgage.uploads.find(u => u.id === upload.id) : null
                const verifiedItems = mortgageUpload ? mortgageUpload?.verifiedData?.items : []
                const mortgageRows = verifiedItems ? verifiedItems.filter(r => r.name === name) : []


                let mortgageRow = mortgageRows.length ? mortgageRows[0] : null
                if (mortgageRows.length > 1) {
                    console.log('has too many rows - using one with value or else newest')
                    let onesWithValues = mortgageRows.filter(it => !!it.verifiedValue)
                    if (onesWithValues > 1) {
                        mortgageRow = onesWithValues.sort((a, b) => {
                            return new Date(b.createdAt) - new Date(a.createdAt)
                        })[0]
                    } else {
                        mortgageRow = onesWithValues[0]
                    }

                }
                return {
                    item,
                    data,
                    save: async (value) => {

                        let [verifiedValue, verifiedValueType] = getVerificationValueType(value, askInFormAnswerTypes[item.type])
                        if (data) {
                            let result = await updateVerifiedRow(data, {
                                verifiedValue,
                                verifiedValueType,
                                verificationOn: dayjs().toISOString(),
                                verificationBy: this.me.sub,
                                _version: mortgageRow._version
                            })
                            mortgage.setMortgage(prevMortgage => {
                                return {
                                    ...prevMortgage,
                                    uploads: prevMortgage.uploads.map(item => {
                                        if (item.id === result.uploadID) {
                                            return {
                                                ...item,
                                                verifiedData: {
                                                    ...item.verifiedData,
                                                    items: item.verifiedData.items.map(it => {
                                                        if (it.id === result.id) {
                                                            return result
                                                        }
                                                        return it
                                                    })
                                                }
                                            }
                                        }
                                        return item
                                    })
                                }
                            })
                            data = result
                        } else {
                            let result = await createVerifiedRow(name, verifiedValue, verifiedValueType)
                            mortgage.setMortgage(prevMortgage => {
                                return {
                                    ...prevMortgage,
                                    uploads: prevMortgage.uploads.map(item => {

                                        if (item.id === upload.id) {
                                            let oldVerifiedData = item?.verifiedData || {items: []}
                                            return {
                                                ...item,
                                                verifiedData: {
                                                    ...oldVerifiedData,
                                                    items: [
                                                        ...item.verifiedData?.items || [], result
                                                    ]
                                                }
                                            }
                                        }
                                        return item
                                    })
                                }
                            })
                            data = result
                        }
                    },
                    update: async (pairs) => {
                        await updateVerifiedRow(data, pairs)
                    },
                    createMessage: async (message) => {
                        await createMessage(data.id, message)
                    }
                }
            }
        }
    }
    getActiveRows = () => {
        return this.rows
    }
    setRows = () => {
        let config = {
            mortgage: this.mortgage,
            section: this.section,
            me: this.me
        }
        this.rows = this.config.getUploadSummaryRows(this.uploads.active, config).sort(sorterByDateName(this.config.sortDate))

    }
    setErrors = () => {
        if (!this.config.hasOwnProperty('getSectionErrors')) {
            this.errors = []
        } else {
            let errors = this.config.getSectionErrors(this.rows, this.mortgage, this.minimums, this.section)
            if (errors.hasOwnProperty('additionalQuestions')) {
                this.additionalQuestions = errors.additionalQuestions
                this.errors = errors.errors
            } else {
                this.errors = errors
            }

            // remove the Invalid Data error, as it's included already
            let allAnswered = this.rows.every(it => !it.errors || it.errors.length === 0 || (it.errors.length === 1 && typeof it.errors[0] === 'string' && it.errors[0].startsWith('Missing statement')))
            if (!allAnswered) {
                this.errors.push(`Data not entered`)
            }

        }
        if (!this.rows.length) {
            this.errors.push(`No active uploads`)
        }
    }
    getErrors = () => {
        return this.errors
    }

    setSectionConfig() {
        const templates = getConfigurationTemplates()
        if (this.section.name.startsWith('assets') && !this.section.isInvestment) {
            this.config = templates.accountStatementConfig
            this.isAccount = true
        }
        if (this.section.name.startsWith('assets') && this.section.isInvestment) {
            this.config = templates.investmentStatementConfig
            this.isAccount = true
        }
        if (this.section.name.startsWith('debts') && this.section.isCredit) {
            this.config = templates.cardStatementConfig
        }
        if (this.section.name.startsWith('properties')) {
            this.config = templates.mortgageStatementConfig
        }
        if (this.section.name.startsWith('debts') && !this.section.isCredit) {
            this.config = templates.loanStatementConfig
        }
        if (this.section.name.startsWith('proof-of-address')) {
            this.config = templates.proofOfAddressConfig
        }
        if (this.section.name.startsWith('current-accounts')) {
            this.config = templates.accountStatementConfig
            this.isAccount = true
        }
        if (this.section.name.startsWith('payslips')) {
            this.config = templates.payslipConfig
        }
        if (this.section.name.startsWith('employment-summary')) {
            this.config = templates.edsConfig
        }
        if (this.section.name.startsWith('gift-certificate')) {
            this.config = templates.giftCertificateConfig
        }
        if (this.section.name.endsWith('gift-letter')) {
            this.config = templates.giftCertificateConfig
        }
        if (this.section.name.startsWith('salary-cert')) {
            this.config = templates.salaryCertConfig
        }
        if (this.section.name.startsWith('identification')) {
            this.config = templates.identificationConfig
        }
        if (this.section.name.startsWith('proof-of-deposit')) {
            this.config = templates.proofOfDepositAvailableConfig
        }
        if (this.section.name.startsWith('deposit-receipt')) {
            this.config = templates.depositReceiptConfig
        }
        if (this.section.name.startsWith('residence-permit')) {
            this.config = templates.residencePermitConfig
        }
        if (!this.config) {
            this.isVerifiable = false
        }
    }

    setUploads() {
        const extendUpload = (upload) => {
            let verified = new VerifiableUpload(upload, this)
            let sortDate = verified.values[this.config.sortDate]
            if (!sortDate && this.config.sortDate === 'fakeYearDate') {
                sortDate = dayjs(verified.values.year, 'YYYY')
            }
            return ({
                ...upload,
                verified,
                sortDate,
                isDisabled: upload.exclude || [UploadStatuses.EXPIRED, UploadStatuses.REJECTED].includes(upload.status)
            })
        }
        if (this.isVerifiable) {
            this.uploads = {
                inactive: this.section.uploads.filter(it => !uploadExcludeFilter(it)).map(it => extendUpload(it)),
                active: this.section.uploads.filter(uploadExcludeFilter).map(it => extendUpload(it))
            }

        } else {
            this.uploads = {
                inactive: this.section.uploads.filter(it => !uploadExcludeFilter(it)),
                active: this.section.uploads.filter(uploadExcludeFilter)
            }
        }

    }

    refreshUploads = () => {

        this.setUploads()
        this.setRows()
        this.setErrors()

    }
    ApplicationQuestions = () => {
        if (!this.section.uploads.length || !this.config || !this.config.SectionApplicationForm) {
            return null
        }
        return this.config.SectionApplicationForm({
            additionalQuestions: this.additionalQuestions,
            section: this.section,
            mortgage: this.mortgage
        })
    }
    useUploadsSummary = () => {

        if (!this.uploads || !this.uploads.active.length || !this.config) {
            return {
                columns: [],
                dataSource: []
            }
        }

        const columns = useMemo(() => {
            let cols = this.config.uploadsSummaryColumns
            let extraColumn = {
                title: 'Actions',
                key: 'actions',
                align: 'right',
                dataIndex: 'actions',
            }
            let extraColumn2 = {
                title: 'Rows',
                key: 'rows',
                align: 'right',
                render: (text, record) => {
                    if (!this.isAccount) {
                        return null
                    }
                    return <ScannedRows size="small" upload={record.upload} mortgage={this.mortgage}
                                        sectionScan={this.handleScan}
                                        fieldName={this.config.analysisPropertyName}>Rows</ScannedRows>
                }
            }
            let anyWithError = this.rows.some(it => it.errors && it.errors.length)
            if (!anyWithError) {
                // remove the errors column, if there are no errors
                let errorIndex = cols.findIndex(it => it.key === 'errors')
                if (errorIndex >= 0) {
                    cols.splice(errorIndex, 1)
                }
            }
            return [
                ...cols, extraColumn, extraColumn2
            ]

        }, [])
        const rowClassName = (record, index) => {
            // Apply the 'shaded-row' class to every other row
            if (record?.tooOld) {
                return 'upload-row-expired'
            }
            if (record?.upload?.examined) {
                return 'upload-row-examined'
            }
            return '';
        };
        let dataSource = this.rows
        useEffect(() => {
            this.performActionsOnRows(this.rows)
            return () => {
                this.cancelPerformActionsTimout()
            }
        }, [dataSource]);
        return {
            columns,
            dataSource,
            rowClassName
        }
        // return (<Table className="my-3" columns={cols} dataSource={dataSource} pagination={false}/>)

    }
    SummaryMessages2 = () => {
        console.log('SUMMARY MESSAGES!!!!')
        const {me} = useBackendUsers()
        return (<>
            {!!this.errors && !!this.errors.length && (<div className="py-15 px-6 d-col gap-6">
                {this.errors.map((it, i) => {
                    if (typeof it === 'string' && it.includes('already in app')) {
                        let message = (<div className="d-row j-between">
                            {it}
                            <ButtonAsync onClick={() => {
                                this.mortgage.mutate({
                                    update: {
                                        target: `progress[${this.section.record.id}]`,
                                        pairs: {
                                            lastDocumentDateDismissed: !this.section.record.lastDocumentDateDismissed
                                        }
                                    }
                                })
                            }}>{this.section.record.lastDocumentDateDismissed ? 'Unblock Msg' : 'Block Msg'}</ButtonAsync>
                        </div>)
                        return (<Alert key={it} message={message}
                                       type={this.section.record.lastDocumentDateDismissed ? 'warning' : 'error'}/>)
                    }

                    if (typeof it === 'string' && it.startsWith('Missing ')) {
                        let message = (<div className="d-row j-between">
                            {it}
                            <ButtonAsync onClick={() => {
                                this.mortgage.mutate({
                                    update: {
                                        target: `progress[${this.section.record.id}]`,
                                        pairs: {
                                            alertTitle: `We need another ${this.section.title}`,
                                            alertBody: [it],
                                            alertDismissed: false,
                                            alertAuthor: me.sub,
                                        }
                                    }
                                })
                            }}>Create Request Another Upload</ButtonAsync>
                        </div>)
                        return (<Alert key={it} message={message} type={"error"}/>)
                    }
                    return (<Alert key={it} message={it} type="error"/>)
                })}
            </div>)}

        </>)
    }
    InternalNote = () => {
        return (<div className="d-row"
                     style={{borderBottom: '1px solid lightgray'}}>
            <div style={{
                fontWeight: 600,
                width: '170px'
            }} className="p-15 shrink-not">
                Internal Note:
            </div>
            <div className="p-15 grow">
                <InternalNote section={this.section}/>
            </div>
        </div>)
    }
    performActionsOnRows = async (rows) => {
        clearTimeout(this.updateMortgageTimer)
        this.updateMortgageTimer = setTimeout(async () => {
            let last = rows[rows.length - 1]
            const first = rows[0]


            let date = last.hasOwnProperty('endDate') ? last.endDate : last[this.config.sortDate]
            let update = []
            if (dayjs.isDayjs(date) && date?.isValid()) {
                let val = this.section.record.lastDocumentDate
                if (val !== date.format('YYYY-MM-DD')) {
                    update.push({
                        target: `progress[${this.section.record.id}]`,
                        pairs: {
                            lastDocumentDate: date.format('YYYY-MM-DD')
                        }
                    })
                }
            }
            const fDate = first.hasOwnProperty('startDate') ? first.startDate : first[this.config.sortDate]
            if (dayjs.isDayjs(fDate) && fDate?.isValid()) {
                let val = this.section.record.firstDocumentDate
                if (val !== fDate.format('YYYY-MM-DD')) {
                    update.push({
                        target: `progress[${this.section.record.id}]`,
                        pairs: {
                            firstDocumentDate: fDate.format('YYYY-MM-DD')
                        }
                    })
                }
            }

            let tooOld = rows.filter(row => !!row.tooOld).map(row => row.upload.id)
            tooOld.forEach(id => {
                update.push({
                    target: `uploads[${id}].status`,
                    value: UploadStatuses.EXPIRED
                })
            })
            if (update.length) {
                await this.mortgage.mutate({update})
            }

        }, 500)
    }
    cancelPerformActionsTimout = () => {
        clearTimeout(this.updateMortgageTimer)
    }

    handleScan = async (data) => {
        if (this.config.hasOwnProperty('saveScanDataToSection')) {
            await this.config.saveScanDataToSection(this.mortgage, this.section, data)
        }
    }


    getSectionErrors = ()=>{

    }
}

